import { sanitizeRichTextHtml } from '../utils/sanitizeHtml';
import ProcessLoadingCard from '../components/ProcessLoadingCard';
// MetadataEditForm.tsx
// NOTE: Update frontend/src/__tests__/MetadataEditForm.test.tsx and run
// `../scripts/test-frontend.sh MetadataEditForm` when editing helper logic.
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { Input, Button, Divider, Tabs, Select, Tooltip, Tag, Space, Form, message, Modal, Dropdown, AutoComplete } from 'antd';
import type { TourProps } from 'antd';
import { QuestionCircleOutlined, BulbOutlined, SaveOutlined, EyeOutlined,
  TranslationOutlined, DeleteOutlined, CopyOutlined, FormOutlined, LockOutlined, CheckOutlined, ExportOutlined, MoreOutlined, PlusOutlined, BranchesOutlined, UploadOutlined, SearchOutlined, ReloadOutlined, VideoCameraOutlined, PictureOutlined } from '@ant-design/icons';

import type { CustomTagProps } from 'rc-select/lib/BaseSelect';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import MetadataTranslationTabContent from '../MetadataTranslationTabContent';
import AudiobookTab from '../AudiobookTab';
import MarketingTab from '../MarketingTab';
import type { MarketingModuleKey } from '../marketing/types';
import { EditBook, ChapterWordCounts, SeriesSummary, FrenchNarrativeTone, TranslationProgressMap, RecentExportEntry, CreditBalance } from '../types';
import { isLanguageBeta } from '../LanguageConfig';
import renderMetadataCharactersTab from '../MetadataCharactersTab';
import FrenchNarrationInfo from '../components/FrenchNarrationInfo';
import BlurbWordCounter from '../components/BlurbWordCounter';
import { RELATIONSHIP_UI_ENABLED } from '../featureFlags';
import { useNavigate } from 'react-router-dom';
import AdminTranslationInsights from '../Admin/AdminTranslationInsights';
import type { AskAIInvocationPayload } from '../AskAIModal';
import axios from 'axios';
import { TranslationMilestoneTrackerProps } from '../components/TranslationMilestoneTracker';
import BookDashboardTab, { type DashboardUtilityAction } from '../BookDashboardTab';
import type { CreditTopUpContext } from '../types/creditTopUp';
import { isTutorialUiSuppressionRequested } from '../utils/tutorialMode';
import { splitLanguagePair } from '../utils/languagePairs';
import { resolveAccountTerms, resolveBookTerms, resolveSeriesTerms } from '../utils/glossaryKeys';
import { hasPendingProofreadReviewForRow } from '../utils/proofreadState';
import {
  INTENDED_AUDIENCE_PRESETS,
  enforceNonfictionAudienceExclusivity,
  splitAndDedupeAudienceValues,
} from '../utils/intendedAudience';
import MetadataHeader from './MetadataHeader';
import MetadataLanguageSelector from './MetadataLanguageSelector';
import OriginalMetadataEditor from './OriginalMetadataEditor';
import LocalizedMetadataWorkspace, { type LocalizedMetadataContentMode } from './LocalizedMetadataWorkspace';
import MetadataIdentitySection from './MetadataIdentitySection';
import MetadataPositioningSection from './MetadataPositioningSection';
import MetadataAutofillReviewModal, { type MetadataAutofillFieldKey } from './MetadataAutofillReviewModal';
import AppTour, { renderAppTourPanel } from '../tours/AppTour';
import { tourTarget, useGuidedTour } from '../tours/useGuidedTour';
import {
  buildSourceReadinessItems,
  type SourceReadinessItem,
} from '../utils/sourceReadiness';
import {
  getBlurbWordCount,
  MAX_BLURB_TRANSLATION_WORDS,
} from '../utils/metadataFreeRuns';

const { TabPane } = Tabs;
const AUTO_FRENCH_TONE_VALUE = 'AI Detect';
const FRENCH_TONE_VALUES: FrenchNarrativeTone[] = ['modern', 'classical', 'present'];
const METADATA_WORKSPACE_TABS = new Set(['dashboard', 'metadata', 'marketing', 'characters', 'audiobook', 'admin']);
const readWorkspaceQueryParam = (key: string, fallback: string): string => {
  if (typeof window === 'undefined') return fallback;
  const value = new URLSearchParams(window.location.search).get(key)?.trim();
  return value || fallback;
};
const isFrenchToneValue = (value: unknown): value is FrenchNarrativeTone => {
  if (typeof value !== 'string') return false;
  return (FRENCH_TONE_VALUES as string[]).includes(value);
};

const BetaTag: React.FC = () => (
  <Tag
    color="orange"
    style={{ marginLeft: 8, fontSize: '0.75em', padding: '0 4px', lineHeight: 1 }}
  >
    BETA
  </Tag>
)

const hasVisibleText = (value: unknown): boolean => {
  const raw = typeof value === 'string' ? value : '';
  return raw
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim().length > 0;
};

const normalizeM2MText = (value: unknown): string => {
  if (Array.isArray(value)) {
    return value.map(normalizeM2MText).filter(Boolean).join(', ');
  }
  if (value && typeof value === 'object') {
    return Object.values(value).map(normalizeM2MText).filter(Boolean).join(', ');
  }
  return String(value || '').replace(/\s+/g, ' ').trim();
};

const dedupeM2MParts = (values: unknown[]): string[] => {
  const seen = new Set<string>();
  const parts: string[] = [];
  values.forEach((value) => {
    const text = normalizeM2MText(value);
    if (!text) return;
    const key = text.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    parts.push(text);
  });
  return parts;
};

const buildM2MGenreDescription = (
  context: any,
  genres: string[],
  tropes: string[],
  keywords: string[],
): string => {
  const genreSignals = context?.genreSignals && typeof context.genreSignals === 'object'
    ? Object.values(context.genreSignals)
    : [];
  const parts = dedupeM2MParts([
    context?.primarySubgenre,
    ...genreSignals,
    ...genres.slice(0, 2),
    ...tropes.slice(0, 4),
    ...keywords.slice(0, 4),
  ]);
  return parts.join(', ').slice(0, 220);
};

const buildM2MNotes = (context: any): string => {
  return normalizeM2MText(context?.translatorNotes).slice(0, 1200);
};

const normalizeMetadataHtmlForDirtyCheck = (value: unknown): string => {
  const raw = typeof value === 'string' ? value : '';
  const normalized = raw
    .replace(/\r\n/g, '\n')
    .replace(/&nbsp;/gi, ' ')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .trim();
  return /^<p><br><\/p>$/i.test(normalized) ? '' : normalized;
};

export interface MetadataWorkspaceProps {
  book: any;
  editBook: any;
  handleInputChange: (key: keyof EditBook, value: string | number | null | undefined, options?: { markDirty?: boolean }) => void;
  handleSave: () => void;
  blurb: string;
  setBlurb: (value: string) => void;
  shouldSuppressBlurbDirty?: () => boolean;
  languages: string[];
  bookLanguage: string;
  setBookLanguage: (value: string) => void;
  genres: string[];
  setGenres: (value: string[]) => void;
  tropes: string[];
  setTropes: (value: string[]) => void;
  keywords: string[];
  setKeywords: (value: string[]) => void;
  intendedAudience: string[];
  setIntendedAudience: (value: string[]) => void;
  toggleMetadataTranslateModal: (preferredTargetLanguage?: string) => void;
  toggleKeywordSuggestionModal: (preferredTargetLanguage?: string) => void;
  exportBlurbToTxt: () => void;
  handleGenerateBlurb: () => void;
  isTitleValid: boolean;
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (value: boolean) => void;
  blurbActiveTabKey: string;
  setBlurbActiveTabKey: (value: string) => void;
  setCurrentLanguagePair: (value: string) => void;
  setIsTranslatedTabActive: (value: boolean) => void;
  isTranslatedTabActive: boolean;
  blurbTranslations: any;
  titleTranslations: any;
  seriesTranslations: any;
  seriesDescriptionTranslations?: Record<string, string>;
  keywordTranslations: any;
  neverTranslateWords: any;
  alwaysTranslateWords: any;
  accountNeverTranslateWords?: { [key: string]: string[] };
  accountAlwaysTranslateWords?: { [key: string]: string[] };
  seriesNeverTranslateWords: { [key: string]: string[] };
  seriesAlwaysTranslateWords: { [key: string]: string[] };
  bookId: string;
  auth: any;
  setNeverTranslateWords?: React.Dispatch<React.SetStateAction<{ [key: string]: string[] }>>;
  setAlwaysTranslateWords?: React.Dispatch<React.SetStateAction<{ [key: string]: string[] }>>;
  setAccountNeverTranslateWords?: React.Dispatch<React.SetStateAction<{ [key: string]: string[] }>>;
  setAccountAlwaysTranslateWords?: React.Dispatch<React.SetStateAction<{ [key: string]: string[] }>>;
  setSeriesTranslateRules?: React.Dispatch<
    React.SetStateAction<{
      alwaysTranslate: Record<string, string[]>;
      neverTranslate: Record<string, string[]>;
    }>
  >;
  handleDeleteTranslatedChapters: (sourceLang: string, targetLang: string) => void;
  handleDeleteProofreadChapters: (sourceLang: string, targetLang: string) => void;
  setIsNeverTranslateModalVisible: (value: boolean) => void;
  setIsAlwaysTranslateModalVisible: (value: boolean) => void;
  setTitleTranslations: (value: any) => void;
  setSeriesTranslations: (value: any) => void;
  setBlurbTranslations: (value: any) => void;
  selectedChapter: any;
  handleAddLanguage: () => void;
  commonGenres: string[];
  handleTranslateBookClick: () => void;
  handleProofreadBookClick: () => void;
  handleExportBookClick: () => void;
  recentExports?: RecentExportEntry[];
  onDownloadRecentExport?: (entry: RecentExportEntry) => Promise<void>;
  onRefreshProgress?: () => void;
  isRefreshingProgress?: boolean;
  toggleAudiobookModal: () => void;
  audiobookVersionOptions?: Array<{
    value: string;
    label: string;
  }>;
  onTranslateText?: (text: string, targetLang: string) => Promise<string>;
  isLangTesterUser: boolean;
  isBetaUser: boolean;
  chapterWordCounts: ChapterWordCounts;
  onTabChange?: (value: string) => void;
  selectedTab?: string;
  languageCovers?: { [language: string]: string };
  onActiveTabChange?: (tabKey: string) => void;
  onMetadataLanguageChange?: (language: string) => void;
  startAudiobookTourSignal?: number;
  onAudiobookTopUpCredits?: (context: CreditTopUpContext) => void;
  startCharactersTourSignal?: number;
  relationshipSummaryCard?: React.ReactNode;
  relationshipFreeRunAvailable?: boolean;
  relationshipAnalysisLoading?: boolean;
  registryRefreshSignal?: number | null;
  registryClearSignal?: number | null;
  apiBase: string;
  resolvedUserId: string;
  onOpenRelationshipEditor: (chapterGuid?: string) => void;
  onOpenRelationshipAnalysis: () => void;
  onCloseRelationshipEditor?: () => void;
  onCloseRelationshipAnalysis?: () => void;
  activeTabSignal?: { tab: string; ts: number } | null;
  metadataLanguageSignal?: { language: string; ts: number } | null;
  seriesOptions: SeriesSummary[];
  selectedSeriesId: string | null;
  onSeriesChange: (value: string | null) => void;
  seriesDescription: string;
  onSeriesDescriptionChange: (value: string) => void;
  seriesInheritance: Record<string, boolean>;
  onSeriesInheritanceChange: (field: string, inherit: boolean) => void;
  onCreateSeries: (name: string, description: string) => Promise<void> | void;
  onCopyMetadata: (sourceBookId: string, fields: string[], applyToSeries: boolean) => Promise<void> | void;
  isSeriesBusy: boolean;
  isAdmin?: boolean;
  onAskAI?: (payload: AskAIInvocationPayload) => void;
  onFrenchToneSaved?: (tone: FrenchNarrativeTone | undefined) => void;
  translationTrackerData?: TranslationMilestoneTrackerProps | null;
  onOpenChapterUploadModal: () => void;
  isChapterUploadDisabled?: boolean;
  chapterUploadDisabledReason?: string;
  handleCoverUpload?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onOpenCoverTranslationForLanguage?: (language: string) => void;
  onOpenCoverHistoryForLanguage?: (language: string) => void;
  translationProgress?: TranslationProgressMap;
  translationLanguages?: string[];
  showTranslationProgress?: boolean;
  onSelectTranslationLanguage?: (language: string) => void;
  ensureMetadataLanguage?: (language: string) => Promise<boolean>;
  onPrepareMetadataTranslateLanguage?: (language: string) => void;
  onPrepareKeywordSuggestionLanguage?: (language: string) => void;
  onOpenProofreadForLanguage?: (language: string) => void;
  onApplyPendingProofreadReviewsForLanguage?: (language: string) => void;
  applyingPendingProofreadLanguage?: string | null;
  creditBalance?: CreditBalance | null;
  fetchCreditBalance?: (userId: string) => Promise<void> | void;
  glossaryScanTrigger?: { pair: string; nonce: number } | null;
  termScanReviewTrigger?: { pair: string; nonce: number } | null;
  onTermScanTopUpCredits?: (context: CreditTopUpContext) => void;
  onRunMetadataAutofill?: (options?: {
    mode?: 'new_upload' | 'existing_suggestions';
    applyMode?: 'empty_fields' | 'suggestions_only';
    forceRegenerate?: boolean;
    applyFields?: string[];
    applyValues?: Record<string, string[]>;
  }) => Promise<any> | any;
  frenchDialogueLegacyEligibilityByPair?: Record<
    string,
    {
      eligible: boolean;
      reasonCode: string;
      pair?: string;
      alreadyRan?: boolean;
      inProgress?: boolean;
      rulesetVersion?: string;
    }
  >;
  onFetchFrenchDialogueLegacyEligibility?: (params: {
    pair: string;
    targetLanguage: string;
    force?: boolean;
  }) => Promise<
    | {
        eligible: boolean;
        reasonCode: string;
        pair?: string;
        alreadyRan?: boolean;
        inProgress?: boolean;
        rulesetVersion?: string;
      }
    | null
  >;
  onRunFrenchDialogueLegacyCorrection?: (params: {
    pair: string;
    targetLanguage: string;
    chapterGuid?: string;
    chapterName?: string;
  }) => void;
  coverSection?: React.ReactNode;
}

export const normalizeValue = (value: string) => value.trim().toLowerCase();

export const splitInputValues = (rawValues: string[]): string[] => {
  return splitAndDedupeAudienceValues(rawValues);
};

export const mergeSeriesValues = (values: string[], seriesValues?: string[]) => {
  const result: string[] = [];
  const seen = new Set<string>();
  const addValue = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    const key = normalizeValue(trimmed);
    if (seen.has(key)) return;
    seen.add(key);
    result.push(trimmed);
  };
  (seriesValues ?? []).forEach(addValue);
  values.forEach(addValue);
  return result;
};

export const buildSeriesSet = (seriesValues?: string[]) =>
  new Set((seriesValues ?? []).map(value => normalizeValue(value)));

export const extractCustomValues = (values: string[], defaultsSet: Set<string>): string[] =>
  values.filter(value => !defaultsSet.has(normalizeValue(value)));

const createTagRenderer =
  (seriesSet: Set<string>) =>
  (tagProps: CustomTagProps) => {
    const { label, value, closable, onClose } = tagProps;
    const isSeriesValue = seriesSet.has(normalizeValue(String(value)));
    const tagContent = (
      <Tag
        key={String(value)}
        className="metadata-compact-tag"
        icon={isSeriesValue ? <LockOutlined /> : undefined}
        color={isSeriesValue ? 'default' : undefined}
        closable={!isSeriesValue && closable}
        onClose={onClose}
        style={{
          marginRight: 4,
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          ...(isSeriesValue ? { color: 'var(--text-secondary)' } : {}),
        }}
      >
        <span className="metadata-compact-tag-label">{label}</span>
      </Tag>
    );
    if (!isSeriesValue) {
      return tagContent;
    }
    return (
      <Tooltip title="Series default (locked)">
        {tagContent}
      </Tooltip>
    );
  };

const METADATA_TAG_MAX_COUNT = 8;
const METADATA_TAG_MAX_TEXT_LENGTH = 24;
const METADATA_KEYWORD_TAG_MAX_COUNT = 12;

const renderMetadataMaxTagPlaceholder = (omittedValues: Array<{ label?: React.ReactNode; value?: unknown }>) => {
  const hiddenLabels = omittedValues
    .map(item => String(item.label ?? item.value ?? '').trim())
    .filter(Boolean);

  return (
    <Tooltip title={hiddenLabels.length ? hiddenLabels.join(', ') : 'More selected values'}>
      <span className="metadata-select-overflow-count">+{omittedValues.length} more</span>
    </Tooltip>
  );
};

const MetadataWorkspace: React.FC<MetadataWorkspaceProps> = ({
  book,
  editBook,
  handleInputChange,
  handleSave,
  blurb,
  setBlurb,
  shouldSuppressBlurbDirty,
  languages,
  bookLanguage,
  setBookLanguage,
  genres,
  setGenres,
  tropes,
  setTropes,
  keywords,
  setKeywords,
  intendedAudience,
  setIntendedAudience,
  toggleMetadataTranslateModal,
  toggleKeywordSuggestionModal,
  exportBlurbToTxt,
  handleGenerateBlurb,
  isTitleValid,
  hasUnsavedChanges,
  setHasUnsavedChanges,
  blurbActiveTabKey,
  setBlurbActiveTabKey,
  setCurrentLanguagePair,
  setIsTranslatedTabActive,
  isTranslatedTabActive,
  blurbTranslations,
  titleTranslations,
  seriesTranslations,
  seriesDescriptionTranslations = {},
  keywordTranslations,
  neverTranslateWords,
  alwaysTranslateWords,
  accountNeverTranslateWords,
  accountAlwaysTranslateWords,
  seriesNeverTranslateWords,
  seriesAlwaysTranslateWords,
  bookId,
  auth,
  setNeverTranslateWords,
  setAlwaysTranslateWords,
  setAccountNeverTranslateWords,
  setAccountAlwaysTranslateWords,
  setSeriesTranslateRules,
  handleDeleteTranslatedChapters,
  handleDeleteProofreadChapters,
  setIsNeverTranslateModalVisible,
  setIsAlwaysTranslateModalVisible,
  setTitleTranslations,
  setSeriesTranslations,
  setBlurbTranslations,
  selectedChapter,
  handleAddLanguage,
  commonGenres,
  handleExportBookClick,
  recentExports = [],
  onDownloadRecentExport = async () => {},
  onRefreshProgress,
  isRefreshingProgress = false,
  toggleAudiobookModal,
  audiobookVersionOptions,
  onTranslateText,
  isLangTesterUser,
  isBetaUser,
  chapterWordCounts,
  onTabChange,
  selectedTab,
  languageCovers,
  onActiveTabChange,
  onMetadataLanguageChange,
  startAudiobookTourSignal,
  onAudiobookTopUpCredits,
  startCharactersTourSignal,
  relationshipSummaryCard,
  relationshipFreeRunAvailable,
  relationshipAnalysisLoading,
  registryRefreshSignal,
  registryClearSignal,
  apiBase,
  resolvedUserId,
  onOpenRelationshipEditor,
  onOpenRelationshipAnalysis,
  onCloseRelationshipEditor,
  onCloseRelationshipAnalysis,
  activeTabSignal,
  metadataLanguageSignal,
  seriesOptions,
  selectedSeriesId,
  onSeriesChange,
  seriesDescription,
  onSeriesDescriptionChange,
  seriesInheritance,
  onSeriesInheritanceChange,
  onCreateSeries,
  isAdmin = false,
  onAskAI,
  onFrenchToneSaved,
  translationTrackerData,
  onOpenChapterUploadModal,
  isChapterUploadDisabled,
  chapterUploadDisabledReason,
  handleCoverUpload,
  onOpenCoverTranslationForLanguage,
  onOpenCoverHistoryForLanguage,
  handleTranslateBookClick,
  handleProofreadBookClick,
  translationProgress,
  translationLanguages,
  showTranslationProgress,
  onSelectTranslationLanguage,
  ensureMetadataLanguage,
  onPrepareMetadataTranslateLanguage,
  onPrepareKeywordSuggestionLanguage,
  onOpenProofreadForLanguage,
  onApplyPendingProofreadReviewsForLanguage,
  applyingPendingProofreadLanguage,
  creditBalance,
  fetchCreditBalance,
  glossaryScanTrigger,
  termScanReviewTrigger,
  onTermScanTopUpCredits,
  onRunMetadataAutofill,
  frenchDialogueLegacyEligibilityByPair,
  onFetchFrenchDialogueLegacyEligibility,
  onRunFrenchDialogueLegacyCorrection,
  coverSection,
}) => {
  const navigate = useNavigate();
  const originalBlurbWordCount = getBlurbWordCount(blurb);
  const originalBlurbOverLimit = originalBlurbWordCount > MAX_BLURB_TRANSLATION_WORDS;
  const relationshipAnalysisStatus = book?.characterRelationshipsAnalysisStatus ?? '';
  // State to track currently selected language/pair
  const [selectedLanguage, setSelectedLanguage] = useState<string>(() => {
    const requested = selectedTab && selectedTab !== 'Original'
      ? selectedTab : readWorkspaceQueryParam('edition', 'original');
    if (requested === 'original') return requested;
    return Array.isArray(book?.translation_pairs) && book.translation_pairs.includes(requested)
      ? requested
      : 'original';
  });
  const [languageSearchValue, setLanguageSearchValue] = useState('');
  const marketingEntryEditionRef = useRef<string | null>(
    readWorkspaceQueryParam('tab', '') === 'marketing' && selectedLanguage !== 'original'
      ? selectedLanguage : null,
  );
  const originalLanguageLabel = useMemo(
    () => (bookLanguage || 'Original').trim() || 'Original',
    [bookLanguage],
  );
  useEffect(() => {
    onMetadataLanguageChange?.(selectedLanguage);
  }, [onMetadataLanguageChange, selectedLanguage]);

  const [isSavingFrenchTone, setIsSavingFrenchTone] = useState(false);
  const [frenchToneRecentlySaved, setFrenchToneRecentlySaved] = useState(false);
  const frenchToneSaveTimeoutRef = useRef<number | null>(null);
  const [isDetectingFrenchTone, setIsDetectingFrenchTone] = useState(false);
  const [frenchToneDetectedAt, setFrenchToneDetectedAt] = useState<string | null>(
    book?.frenchNarrativeToneDetectedAt ?? null
  );
  useEffect(() => {
    setFrenchToneDetectedAt(book?.frenchNarrativeToneDetectedAt ?? null);
  }, [book?.frenchNarrativeToneDetectedAt]);
  const [activeTab, setActiveTab] = useState(() => {
    if (readWorkspaceQueryParam('tab', '') === 'marketing') return 'marketing';
    const requested = readWorkspaceQueryParam('section', 'dashboard');
    return METADATA_WORKSPACE_TABS.has(requested) && (requested !== 'admin' || isAdmin)
      ? requested
      : 'dashboard';
  });
  const [marketingActionSignal, setMarketingActionSignal] = useState<{
    action: 'generate' | 'refresh' | 'copy';
    ts: number;
  } | null>(null);
  const [activeMarketingModule, setActiveMarketingModule] = useState<MarketingModuleKey>('source_kit');
  const metadataTabShellRef = useRef<HTMLDivElement | null>(null);
  const [glossaryScanRequest, setGlossaryScanRequest] = useState<{ pair: string; nonce: number } | null>(null);
  const [glossaryScanReviewRequest, setGlossaryScanReviewRequest] = useState<{ pair: string; nonce: number } | null>(null);
  const lastGlossaryTriggerRef = useRef<number | null>(null);
  const [dashboardOverflowActions, setDashboardOverflowActions] = useState<DashboardUtilityAction[]>([]);
  const handleDashboardOverflowActionsChange = useCallback((actions: DashboardUtilityAction[]) => {
    setDashboardOverflowActions((previous) => {
      const summarize = (items: DashboardUtilityAction[]) => items.map((item) => ({
        key: item.key,
        signature: item.signature || '',
        label: typeof item.label === 'string' ? item.label : '',
        disabled: Boolean(item.disabled),
        disabledReason: item.disabledReason || '',
        items: item.items?.map((child) => `${child.key}:${child.label}`).join('|') || '',
      }));
      return JSON.stringify(summarize(previous)) === JSON.stringify(summarize(actions))
        ? previous
        : actions;
    });
  }, []);
  useEffect(() => {
    if (!glossaryScanTrigger?.nonce) {
      return;
    }
    if (lastGlossaryTriggerRef.current === glossaryScanTrigger.nonce) {
      return;
    }
    lastGlossaryTriggerRef.current = glossaryScanTrigger.nonce;
    setGlossaryScanRequest(glossaryScanTrigger);
    if (glossaryScanTrigger.pair && glossaryScanTrigger.pair !== selectedLanguage) {
      setSelectedLanguage(glossaryScanTrigger.pair);
      setBlurbActiveTabKey(glossaryScanTrigger.pair);
      setCurrentLanguagePair(glossaryScanTrigger.pair);
      setIsTranslatedTabActive(glossaryScanTrigger.pair !== 'original');
      if (onTabChange) {
        onTabChange(glossaryScanTrigger.pair === 'original' ? 'Original' : glossaryScanTrigger.pair);
      }
    }
  }, [
    glossaryScanTrigger,
    selectedLanguage,
    setBlurbActiveTabKey,
    setCurrentLanguagePair,
    setIsTranslatedTabActive,
    onTabChange,
  ]);
  const [isAudienceInfoModalVisible, setIsAudienceInfoModalVisible] = useState(false);
  const [metadataAutofillLoading, setMetadataAutofillLoading] = useState(false);
  const [metadataAutofillConfirmVisible, setMetadataAutofillConfirmVisible] = useState(false);
  const [metadataAutofillReviewVisible, setMetadataAutofillReviewVisible] = useState(false);
  const [translationContextModalVisible, setTranslationContextModalVisible] = useState(false);
  const metadataAutofillReviewTour = useGuidedTour({
    userId: resolvedUserId,
    apiBase,
    flag: 'metadataAutofillReviewTourSeen',
    enabled: metadataAutofillReviewVisible,
    autoStart: true,
    ready: metadataAutofillReviewVisible,
  });
  const metadataAutofillReviewTourSteps = useMemo<TourProps['steps']>(() => ([
    {
      title: 'Review the saved suggestions',
      description: renderAppTourPanel('This summary shows how many reviewed values are ready. Nothing changes on the book until you apply them.'),
      target: tourTarget('metadata-autofill-review-summary'),
      placement: 'bottom',
    },
    {
      title: 'Apply one group at a time',
      description: renderAppTourPanel('Each row is a metadata group, such as genres, tropes, keywords, or audience labels. Use the row button when you only want that group.'),
      target: tourTarget('metadata-autofill-review-first-row', 'metadata-autofill-review-list'),
      placement: 'bottom',
    },
    {
      title: 'Edit the values',
      description: renderAppTourPanel('Remove suggestions you do not want, or type your own genre, trope, keyword, or audience value and press Enter.'),
      target: tourTarget('metadata-autofill-review-first-values', 'metadata-autofill-review-first-row'),
      placement: 'top',
    },
    {
      title: 'Apply the reviewed metadata',
      description: renderAppTourPanel('This footer action saves all reviewed values across every group in the modal.'),
      target: tourTarget('metadata-autofill-review-apply-selected', 'metadata-autofill-review-summary'),
      placement: 'top',
    },
  ]), []);
  const selectedSeriesSummary = useMemo(() => {
    if (selectedSeriesId) {
      const direct = seriesOptions.find(series => series.seriesId === selectedSeriesId);
      if (direct) return direct;
    }
    const seriesName = (editBook.series || '').trim().toLowerCase();
    if (!seriesName) return null;
    return (
      seriesOptions.find(option => (option.name || '').trim().toLowerCase() === seriesName) ||
      null
    );
  }, [editBook.series, selectedSeriesId, seriesOptions]);
  const seriesDefaultValues = useMemo(() => {
    if (!selectedSeriesSummary) return null;
    const defaults = selectedSeriesSummary.defaults || {};
    const toArray = (value: any): string[] => {
      if (Array.isArray(value)) {
        return value
          .map(item => (typeof item === 'string' ? item.trim() : ''))
          .filter(item => !!item);
      }
      if (typeof value === 'string') {
        return value
          .split(',')
          .map(item => item.trim())
          .filter(item => !!item);
      }
      return [];
    };
    const normalize = (value: any): string => (typeof value === 'string' ? value.trim() : '');
    return {
      author: normalize(defaults.author ?? selectedSeriesSummary.author),
      genre: toArray(defaults.genre),
      tropes: toArray(defaults.tropes),
      keywords: toArray(defaults.keywords),
      intendedAudience: toArray(defaults.intendedAudience),
    };
  }, [selectedSeriesSummary]);
  const seriesGenreDefaults = useMemo(() => seriesDefaultValues?.genre ?? [], [seriesDefaultValues?.genre]);
  const seriesGenreSet = useMemo(() => buildSeriesSet(seriesGenreDefaults), [seriesGenreDefaults]);
  const displayedGenres = useMemo(
    () => mergeSeriesValues(genres, seriesGenreDefaults),
    [genres, seriesGenreDefaults]
  );
  const seriesTropesDefaults = useMemo(() => seriesDefaultValues?.tropes ?? [], [seriesDefaultValues?.tropes]);
  const seriesTropesSet = useMemo(() => buildSeriesSet(seriesTropesDefaults), [seriesTropesDefaults]);
  const displayedTropes = useMemo(
    () => mergeSeriesValues(tropes, seriesTropesDefaults),
    [tropes, seriesTropesDefaults]
  );
  const seriesKeywordsDefaults = useMemo(
    () => seriesDefaultValues?.keywords ?? [],
    [seriesDefaultValues?.keywords]
  );
  const seriesKeywordsSet = useMemo(() => buildSeriesSet(seriesKeywordsDefaults), [seriesKeywordsDefaults]);
  const displayedKeywords = useMemo(
    () => mergeSeriesValues(keywords, seriesKeywordsDefaults),
    [keywords, seriesKeywordsDefaults]
  );
  const originalKeywordSuggestionsCoveredByAutofill = selectedLanguage === 'original'
    && book?.metadataAutofill?.status === 'completed'
    && (
      Array.isArray(book.metadataAutofill?.suggestedFields?.keywords)
      || Array.isArray(book.metadataAutofill?.appliedValues?.keywords)
      || (
        Array.isArray(book.metadataAutofill?.appliedFields)
        && book.metadataAutofill.appliedFields.includes('keywords')
      )
    );
  const seriesIntendedDefault = useMemo(
    () => splitInputValues(seriesDefaultValues?.intendedAudience ?? []),
    [seriesDefaultValues?.intendedAudience]
  );
  const seriesIntendedSet = useMemo(() => buildSeriesSet(seriesIntendedDefault), [seriesIntendedDefault]);
  const displayedIntendedAudience = useMemo(
    () => mergeSeriesValues(splitInputValues(intendedAudience ?? []), seriesIntendedDefault),
    [intendedAudience, seriesIntendedDefault]
  );
  const positioningReadiness = useMemo(
    () => {
      const hasGenres = displayedGenres.length > 0;
      const hasTropes = displayedTropes.length > 0;
      const hasAudience = displayedIntendedAudience.length > 0;

      if (hasGenres && hasTropes && hasAudience) {
        return { label: 'Ready', tone: 'ready' as const };
      }
      if (hasGenres && hasTropes && !hasAudience) {
        return { label: 'Needs audience', tone: 'needed' as const };
      }
      return { label: 'Needs details', tone: 'needed' as const };
    },
    [displayedGenres.length, displayedIntendedAudience.length, displayedTropes.length]
  );
  const discoverabilityReadiness = useMemo(
    () => (displayedKeywords.length > 0
      ? { label: 'Keywords ready', tone: 'ready' as const }
      : { label: 'Keywords needed', tone: 'needed' as const }),
    [displayedKeywords.length]
  );
  const readerDiscoveryReadiness = useMemo(
    () => {
      if (positioningReadiness.tone === 'ready' && discoverabilityReadiness.tone === 'ready') {
        return { label: 'Ready', tone: 'ready' as const };
      }
      if (positioningReadiness.tone !== 'ready' && discoverabilityReadiness.tone !== 'ready') {
        return { label: 'Needs targeting and keywords', tone: 'needed' as const };
      }
      if (positioningReadiness.tone !== 'ready') {
        return positioningReadiness;
      }
      return discoverabilityReadiness;
    },
    [discoverabilityReadiness, positioningReadiness]
  );
  const handleOriginalMetadataReadinessClick = useCallback((item: Pick<SourceReadinessItem, 'targetId' | 'focusSelector'>) => {
    const target = document.getElementById(item.targetId);
    if (!target) {
      return;
    }
    target.scrollIntoView({ block: 'start', behavior: 'smooth' });
    window.setTimeout(() => {
      const preferredTarget = item.focusSelector
        ? document.querySelector(item.focusSelector)
        : null;
      const fallbackTarget = target.querySelector(
        'input, textarea, button, [role="button"], [contenteditable="true"], [tabindex]:not([tabindex="-1"])',
      );
      const focusTarget = preferredTarget || fallbackTarget || target;
      if (focusTarget instanceof HTMLElement) {
        focusTarget.focus({ preventScroll: true });
      }
    }, 120);
  }, []);
  const handleSeriesListChange = (nextValues: string[], defaults: string[], setter: (values: string[]) => void) => {
    const deduped = splitInputValues(nextValues);
    const extras = extractCustomValues(deduped, buildSeriesSet(defaults));
    setter(extras);
    setHasUnsavedChanges(true);
  };
  const handleIntendedAudienceChange = (vals: string[]) => {
    const deduped = enforceNonfictionAudienceExclusivity(vals, seriesIntendedDefault);
    const extras = extractCustomValues(deduped, seriesIntendedSet);
    setIntendedAudience(extras);
    setHasUnsavedChanges(true);
  };

  useEffect(() => {
    if (!onActiveTabChange) return;
    onActiveTabChange(activeTab);
  }, [activeTab, onActiveTabChange]);

  useEffect(() => {
    if (activeTab !== 'metadata') return;
    if (typeof window === 'undefined') return;
    const frame = window.requestAnimationFrame(() => {
      const root = metadataTabShellRef.current;
      const workspaceScroll = root?.closest('.book-detail-workspace-scroll') as HTMLElement | null;
      const activePane = root?.querySelector('.ant-tabs-tabpane-active') as HTMLElement | null;
      const metadataContent = activePane?.querySelector('.metadata-content-scroll') as HTMLElement | null;
      if (workspaceScroll) {
        workspaceScroll.scrollTop = 0;
      }
      if (activePane) {
        activePane.scrollTop = 0;
      }
      if (metadataContent) {
        metadataContent.scrollTop = 0;
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [activeTab]);

  const lastAppliedTabSignalRef = useRef<number | null>(null);

  useEffect(() => {
    if (!activeTabSignal?.ts) return;
    if (lastAppliedTabSignalRef.current === activeTabSignal.ts) return;
    lastAppliedTabSignalRef.current = activeTabSignal.ts;
    if (activeTabSignal.tab) {
      setActiveTab(activeTabSignal.tab);
    }
  }, [activeTabSignal]);

  const handleOpenMetadataTab = () => {
    setActiveTab('metadata');
  };

  const handleOpenAudiobookTab = () => {
    setActiveTab('audiobook');
  };

  const handleOpenCharactersTab = () => {
    setActiveTab('characters');
  };

  const triggerMarketingAction = (action: 'generate' | 'refresh' | 'copy') => {
    setMarketingActionSignal({ action, ts: Date.now() });
  };

  const handleReturnToOriginalDashboard = () => {
    handleLanguageChange('original');
  };

  const handleOpenDashboardForLanguage = (targetLanguage: string) => {
    setActiveTab('dashboard');
    const pair = buildLanguagePair(targetLanguage);
    if (pair === 'original') {
      handleLanguageChange('original');
      return;
    }
    if (ensureMetadataLanguage) {
      void ensureMetadataLanguage(targetLanguage).then((shouldOpen) => {
        if (!shouldOpen) return;
        handleLanguageChange(pair);
      });
      return;
    }
    handleLanguageChange(pair);
  };

  const buildLanguagePair = (targetLanguage: string) => {
    const cleanTarget = (targetLanguage || '').trim();
    const sourceLanguage = (bookLanguage || book?.language || 'English').trim();
    if (!cleanTarget || cleanTarget === sourceLanguage) {
      return 'original';
    }
    return `${sourceLanguage}-${cleanTarget}`;
  };

  const openMetadataForLanguage = (targetLanguage: string) => {
    setActiveTab('metadata');
    const pair = buildLanguagePair(targetLanguage);
    if (pair === 'original') {
      handleLanguageChange('original');
      return;
    }
    if (ensureMetadataLanguage) {
      void ensureMetadataLanguage(targetLanguage).then((shouldOpen) => {
        if (!shouldOpen) return;
        handleLanguageChange(pair);
      });
      return;
    }
    handleLanguageChange(pair);
  };

  const openMetadataTranslateForLanguage = (targetLanguage: string) => {
    if (onPrepareMetadataTranslateLanguage) {
      onPrepareMetadataTranslateLanguage(targetLanguage);
    }
    openMetadataForLanguage(targetLanguage);
    window.setTimeout(() => toggleMetadataTranslateModal(targetLanguage), 0);
  };

  const openGlossaryScanForLanguage = (targetLanguage: string) => {
    const pair = buildLanguagePair(targetLanguage);
    if (pair === 'original') {
      handleLanguageChange('original');
      return;
    }
    const requestScan = () =>
      setGlossaryScanRequest((prev) => ({ pair, nonce: (prev?.nonce ?? 0) + 1 }));
    setActiveTab('metadata');
    if (ensureMetadataLanguage) {
      void ensureMetadataLanguage(targetLanguage).then((shouldOpen) => {
        if (!shouldOpen) return;
        handleLanguageChange(pair);
        requestScan();
      });
      return;
    }
    handleLanguageChange(pair);
    requestScan();
  };

  const openGlossaryScanReviewForLanguage = (targetLanguage: string) => {
    const pair = buildLanguagePair(targetLanguage);
    if (pair === 'original') {
      handleLanguageChange('original');
      return;
    }
    const requestReview = () =>
      setGlossaryScanReviewRequest((prev) => ({ pair, nonce: (prev?.nonce ?? 0) + 1 }));
    setActiveTab('metadata');
    if (ensureMetadataLanguage) {
      void ensureMetadataLanguage(targetLanguage).then((shouldOpen) => {
        if (!shouldOpen) return;
        handleLanguageChange(pair);
        requestReview();
      });
      return;
    }
    handleLanguageChange(pair);
    requestReview();
  };

  const openKeywordSuggestionsForLanguage = (targetLanguage: string) => {
    if (onPrepareKeywordSuggestionLanguage) {
      onPrepareKeywordSuggestionLanguage(targetLanguage);
    }
    openMetadataForLanguage(targetLanguage);
    window.setTimeout(() => toggleKeywordSuggestionModal(targetLanguage), 0);
  };

  const { targetLang: selectedMetadataTargetLanguage } = splitLanguagePair(selectedLanguage);
  const selectedMetadataGlossaryRuleCount = selectedMetadataTargetLanguage
    ? [
        resolveBookTerms(neverTranslateWords, selectedLanguage),
        resolveBookTerms(alwaysTranslateWords, selectedLanguage),
        resolveSeriesTerms(seriesNeverTranslateWords, selectedMetadataTargetLanguage, selectedLanguage),
        resolveSeriesTerms(seriesAlwaysTranslateWords, selectedMetadataTargetLanguage, selectedLanguage),
        resolveAccountTerms(accountNeverTranslateWords, selectedMetadataTargetLanguage),
        resolveAccountTerms(accountAlwaysTranslateWords, selectedMetadataTargetLanguage),
      ].reduce((count, terms) => count + terms.length, 0)
    : 0;
  const selectedMetadataGlossaryReady = selectedMetadataGlossaryRuleCount > 0;

  const renderTranslateBookButton = (tourId?: string) => (
    <Button
      data-tour-id={tourId}
      data-testid="tutorial:translation:open-book"
      aria-label="Translate book"
      icon={<TranslationOutlined />}
      onClick={handleTranslateBookClick}
    >
      Translate
    </Button>
  );

  const runMetadataAutofillSuggestions = async () => {
    if (!onRunMetadataAutofill) {
      message.error('AI metadata fill is unavailable.');
      return;
    }
    setMetadataAutofillConfirmVisible(false);
    setMetadataAutofillLoading(true);
    try {
      const result = await onRunMetadataAutofill({
        mode: 'existing_suggestions',
        applyMode: 'suggestions_only',
        forceRegenerate: true,
      });
      if (result?.status === 'success') {
        message.success('AI metadata suggestions generated.');
        setMetadataAutofillReviewVisible(true);
      }
    } finally {
      setMetadataAutofillLoading(false);
    }
  };

  const handleMetadataAutofillAction = async () => {
    if (!onRunMetadataAutofill) {
      message.error('AI metadata fill is unavailable.');
      return;
    }
    if (book?.metadataAutofill?.status === 'completed' && book?.metadataAutofill?.suggestedFields) {
      setMetadataAutofillReviewVisible(true);
      return;
    }
    setMetadataAutofillConfirmVisible(true);
  };

  const renderMetadataTranslateButton = (tourId?: string) => (
    <Button
      data-tour-id={tourId}
      data-testid="tutorial:metadata:translate-actions"
      aria-label="Translate metadata"
      icon={<TranslationOutlined />}
      onClick={() => toggleMetadataTranslateModal(selectedMetadataTargetLanguage || undefined)}
    >
      Translate metadata
    </Button>
  );

  const renderOverflowActions = (
    items: any[],
    onClick: (params: { key: string }) => void | Promise<void>,
    label = 'More actions',
  ) => {
    const visibleItems = items.filter(Boolean);
    if (!visibleItems.length) return null;
    if (visibleItems.length === 1 && !visibleItems[0].children) {
      const item = visibleItems[0];
      return (
        <Button
          icon={item.icon}
          className="metadata-header-more-actions"
          aria-label={typeof item.label === 'string' ? item.label : undefined}
          disabled={item.disabled}
          onClick={() => onClick({ key: String(item.key) })}
        >
          {item.label}
        </Button>
      );
    }
    return (
      <Dropdown
        menu={{ items: visibleItems, onClick }}
        trigger={['click']}
        placement="bottomRight"
      >
        <Button
          icon={<MoreOutlined />}
          className="metadata-header-more-actions"
          aria-label={label}
          data-testid="tutorial:metadata:more-actions"
        >
          {label}
        </Button>
      </Dropdown>
    );
  };

  const renderHeaderActions = () => {
    if (activeTab === 'dashboard') {
      const languageDeletionAction = {
        key: 'delete-language-chapters',
        icon: <DeleteOutlined />,
        label: 'Delete translated/proofread chapters',
        children: [
          {
            key: 'delete-translated',
            label: 'Delete Translated Chapters',
          },
          {
            key: 'delete-proofread',
            label: 'Delete Proofread Chapters',
          },
        ],
      };
      const dashboardOverflowItems = selectedLanguage !== 'original' && dashboardOverflowActions.length
        ? [
          ...dashboardOverflowActions.map((action) => ({
            key: action.key,
            icon: action.icon,
            disabled: action.disabled,
            label: action.disabledReason ? (
              <Tooltip title={action.disabledReason} placement="left">
                <span>{action.label}</span>
              </Tooltip>
            ) : action.label,
            children: action.items?.map((item) => ({
              key: item.key,
              label: item.label,
            })),
          })),
          languageDeletionAction,
        ]
        : [
          {
            key: 'edit-metadata',
            icon: <FormOutlined />,
            label: 'Edit metadata',
          },
          {
            key: 'add-language',
            icon: <PlusOutlined />,
            label: 'Add language',
          },
          ...(RELATIONSHIP_UI_ENABLED
            ? [{
                key: 'characters',
                icon: <BranchesOutlined />,
                label: 'View characters',
              }]
            : []),
          {
            key: 'upload-chapters',
            icon: <UploadOutlined />,
            label: chapterUploadDisabledReason ? (
              <Tooltip title={chapterUploadDisabledReason} placement="left">
                <span data-tour-id="metadata-upload-chapters">Upload chapters</span>
              </Tooltip>
            ) : (
              <span data-tour-id="metadata-upload-chapters">Upload chapters</span>
            ),
            disabled: isChapterUploadDisabled,
          },
          {
            key: 'support',
            icon: <QuestionCircleOutlined />,
            label: 'Support articles',
          },
          ...(selectedLanguage !== 'original' ? [languageDeletionAction] : []),
        ];
      const handleDashboardOverflowClick = ({ key }: { key: string }) => {
        if (selectedLanguage !== 'original' && dashboardOverflowActions.length) {
          const directAction = dashboardOverflowActions.find((action) => action.key === key);
          if (directAction) {
            directAction.onClick?.();
            return;
          }
          const parentAction = dashboardOverflowActions.find((action) =>
            action.items?.some((item) => item.key === key),
          );
          if (parentAction) {
            parentAction.onItemClick?.(key);
            return;
          }
        }
        if (key === 'delete-translated' || key === 'delete-proofread') {
          const { sourceLang, targetLang } = splitLanguagePair(selectedLanguage);
          if (key === 'delete-translated') {
            handleDeleteTranslatedChapters(sourceLang, targetLang);
          } else {
            handleDeleteProofreadChapters(sourceLang, targetLang);
          }
          return;
        }
        if (key === 'edit-metadata') {
          handleOpenMetadataTab();
          return;
        }
        if (key === 'add-language') {
          handleAddLanguage();
          return;
        }
        if (key === 'characters') {
          handleOpenCharactersTab();
          return;
        }
        if (key === 'upload-chapters') {
          if (!isChapterUploadDisabled) {
            onOpenChapterUploadModal();
          }
          return;
        }
        if (key === 'support') {
          navigate('/support?tab=articles');
        }
      };
      return (
        <div data-tour-id="dashboard-quick-links" className="metadata-header-action-group metadata-header-action-group--quick">
          <div className="metadata-header-primary-actions" data-testid="tutorial:book-detail:book-actions">
            <Tooltip title="Open book translation options">
              {renderTranslateBookButton()}
            </Tooltip>
            <Tooltip title="Proofread this book">
              <Button
                icon={<CheckOutlined />}
                onClick={handleProofreadBookClick}
                data-testid="tutorial:proofread:open-book"
                aria-label="Proofread book"
              >
                Proofread whole book
              </Button>
            </Tooltip>
            <Tooltip title="Export this book">
              <Button
                icon={<ExportOutlined />}
                onClick={handleExportBookClick}
                data-testid="tutorial:export:open-book"
                aria-label="Export book"
              >
                Export
              </Button>
            </Tooltip>
          </div>
          {renderOverflowActions(dashboardOverflowItems, handleDashboardOverflowClick, 'More')}
        </div>
      );
    }

    if (activeTab === 'metadata') {
      const showSourceMetadataAutofillAction = selectedLanguage === 'original' && Boolean(onRunMetadataAutofill);
      const showTranslatedMetadataUtilities = selectedLanguage !== 'original' && Boolean(selectedMetadataTargetLanguage);
      const metadataOverflowItems = [
        ...(showTranslatedMetadataUtilities
          ? [
              {
                key: 'glossary-scan',
                icon: selectedMetadataGlossaryReady ? <EyeOutlined /> : <SearchOutlined />,
                label: selectedMetadataGlossaryReady ? 'Review AI Glossary Scan' : 'Start AI Glossary Scan',
              },
              {
                key: 'suggest-keywords',
                icon: <BulbOutlined />,
                label: 'Suggest keywords',
              },
            ]
          : []),
        ...(selectedLanguage !== 'original'
          ? [
              {
                key: 'delete',
                icon: <DeleteOutlined />,
                label: (
                  <span
                    data-tour-id="metadata-delete-chapters"
                    data-testid="tutorial:metadata:delete-chapters-menu"
                  >
                    Delete translated/proofread chapters
                  </span>
                ),
                children: [
                  {
                    key: 'delete-translated',
                    label: 'Delete Translated Chapters',
                  },
                  {
                    key: 'delete-proofread',
                    label: 'Delete Proofread Chapters',
                  },
                ],
              },
            ]
          : []),
      ];
      const handleMetadataOverflowClick = async ({ key }: { key: string }) => {
        if (key === 'glossary-scan' && selectedMetadataTargetLanguage) {
          if (selectedMetadataGlossaryReady) {
            openGlossaryScanReviewForLanguage(selectedMetadataTargetLanguage);
          } else {
            openGlossaryScanForLanguage(selectedMetadataTargetLanguage);
          }
          return;
        }
        if (key === 'suggest-keywords' && selectedMetadataTargetLanguage) {
          openKeywordSuggestionsForLanguage(selectedMetadataTargetLanguage);
          return;
        }
        if (key === 'delete-translated' || key === 'delete-proofread') {
          const { sourceLang: s, targetLang: t } = splitLanguagePair(selectedLanguage);
          if (key === 'delete-translated') {
            handleDeleteTranslatedChapters(s, t);
          } else {
            handleDeleteProofreadChapters(s, t);
          }
        }
      };
      return (
        <div data-tour-id="metadata-actions" className="metadata-header-action-group">
          <div className="metadata-header-primary-actions">
            {selectedLanguage === 'original' && hasUnsavedChanges && (
              <Tooltip
                title={
                  originalBlurbOverLimit
                    ? `Blurb must be ${MAX_BLURB_TRANSLATION_WORDS.toLocaleString()} words or fewer before saving.`
                    : 'Save metadata changes'
                }
              >
                <Button
                  type="primary"
                  className={isTitleValid ? 'pulse-save-soft' : undefined}
                  onClick={handleSave}
                  disabled={!isTitleValid || originalBlurbOverLimit}
                  aria-label="Save metadata"
                  data-testid="tutorial:metadata:save-button"
                  icon={<SaveOutlined />}
                >
                  Save metadata
                </Button>
              </Tooltip>
            )}
            {showSourceMetadataAutofillAction && (
              <Tooltip title={book?.metadataAutofill?.status === 'completed' ? 'Review AI metadata suggestions' : 'Generate AI metadata suggestions'}>
                <Button
                  icon={<BulbOutlined />}
                  onClick={handleMetadataAutofillAction}
                  loading={metadataAutofillLoading}
                  disabled={metadataAutofillLoading}
                  data-tour-id="metadata-ai-fill"
                  data-testid="tutorial:metadata:ai-suggest"
                >
                  {book?.metadataAutofill?.status === 'completed' ? 'Review AI metadata' : 'Suggest metadata'}
                </Button>
              </Tooltip>
            )}
            <Tooltip title="Translate title, series, and blurb metadata">
              {renderMetadataTranslateButton('metadata-translate')}
            </Tooltip>
          </div>
          {renderOverflowActions(metadataOverflowItems, handleMetadataOverflowClick, 'More')}
        </div>
      );
    }

    if (activeTab === 'marketing') {
      if (activeMarketingModule === 'm2m_reports') {
        return (
          <div data-tour-id="metadata-actions" className="metadata-header-action-group">
            <div className="metadata-header-primary-actions">
              <Tooltip title="Refresh Deep Dive report status">
                <Button
                  aria-label="Refresh Marketing Data"
                  icon={<ReloadOutlined />}
                  onClick={() => triggerMarketingAction('refresh')}
                >
                  Refresh
                </Button>
              </Tooltip>
            </div>
          </div>
        );
      }

      return (
        <div data-tour-id="metadata-actions" className="metadata-header-action-group">
          <div className="metadata-header-primary-actions">
            <Tooltip title={
              activeMarketingModule === 'characters' || activeMarketingModule === 'character_assets'
                ? 'Create a character image'
                : 'Create a marketing asset'
            }>
              <Button
                aria-label="Create Marketing Asset"
                icon={activeMarketingModule === 'characters' || activeMarketingModule === 'character_assets'
                  ? <PictureOutlined />
                  : <VideoCameraOutlined />}
                onClick={() => triggerMarketingAction('generate')}
              >
                {activeMarketingModule === 'characters' || activeMarketingModule === 'character_assets'
                  ? 'Create character image'
                  : 'Create asset'}
              </Button>
            </Tooltip>
            <Tooltip title="Refresh Marketing Data">
              <Button
                aria-label="Refresh Marketing Data"
                icon={<ReloadOutlined />}
                onClick={() => triggerMarketingAction('refresh')}
              >
                Refresh
              </Button>
            </Tooltip>
          </div>
        </div>
      );
    }

    return null;
  };

  // Add state to track audiobook loading status
  const [isAudiobookLoading, setIsAudiobookLoading] = useState(false);
  const openSeriesManager = (focus?: 'description-translations') => {
    const params = new URLSearchParams();
    if (editBook.series && editBook.series.trim()) {
      params.set('seriesName', editBook.series.trim());
    }
    if (selectedSeriesId) {
      params.set('seriesId', selectedSeriesId);
    }
    if (!params.has('seriesName') && !params.has('seriesId')) {
      message.info('Add or select a series to open series settings.');
      return;
    }
    if (focus) {
      params.set('focus', focus);
    }
    navigate(`/series/manage?${params.toString()}`);
  };

  // Coachmark state and refs
  // Get all available language options
  type LanguageOption = { value: string; label: string };
  const languageOptions = useMemo(() => {
    const options: LanguageOption[] = [{ value: 'original', label: originalLanguageLabel }];

    if (book.translation_pairs && !selectedChapter) {
      const sortedPairs: LanguageOption[] = book.translation_pairs
        .map((pair: string) => {
          const label = splitLanguagePair(pair).targetLang.trim();
          return {
            value: pair,
            label,
          };
        })
        .filter((option: LanguageOption) => option.label)
        .sort((a: LanguageOption, b: LanguageOption) =>
          a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
        );
      options.push(...sortedPairs);
    }

    return options;
  }, [book.translation_pairs, originalLanguageLabel, selectedChapter]);

  const resolveRequestedLanguage = useCallback((requestedValue: string): string => {
    const requested = (requestedValue || '').trim();
    if (!requested) return '';
    const normalized = requested.toLowerCase() === 'original' ? 'original' : requested;
    const normalizedLower = normalized.toLowerCase();
    const exactValueMatch = languageOptions.find((option) => option.value.toLowerCase() === normalizedLower);
    if (exactValueMatch) return exactValueMatch.value;
    const labelMatch = languageOptions.find((option) => option.label.toLowerCase() === normalizedLower);
    if (labelMatch) return labelMatch.value;
    return normalized;
  }, [languageOptions]);


  // Handle language change
  const handleLanguageChange = useCallback((value: string) => {
    setSelectedLanguage(value);
    setBlurbActiveTabKey(value);
    setCurrentLanguagePair(value);
    setIsTranslatedTabActive(value !== 'original');
    setLanguageSearchValue('');

    // Sync with parent component's selectedTab
    if (onTabChange) {
      if (value === 'original') {
        onTabChange('Original');
      } else {
        // value is in format "SourceLang-TargetLang"
        onTabChange(value);
      }
    }
  }, [onTabChange, setBlurbActiveTabKey, setCurrentLanguagePair, setIsTranslatedTabActive]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    params.set('edition', selectedLanguage || 'original');
    params.set('section', activeTab);
    const nextSearch = params.toString();
    const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash}`;
    window.history.replaceState(window.history.state, '', nextUrl);
  }, [activeTab, selectedLanguage]);

  const lastMetadataLanguageSignalRef = useRef<number | null>(null);

  useEffect(() => {
    if (isTutorialUiSuppressionRequested()) return;
    if (!metadataLanguageSignal?.ts) return;
    if (lastMetadataLanguageSignalRef.current === metadataLanguageSignal.ts) return;
    const requested = metadataLanguageSignal.language;
    if (!requested) return;
    const resolved = resolveRequestedLanguage(requested);
    if (!resolved) return; // The book's language options may still be loading.
    lastMetadataLanguageSignalRef.current = metadataLanguageSignal.ts;
    if (resolved && resolved !== selectedLanguage) {
      handleLanguageChange(resolved);
    }
  }, [metadataLanguageSignal, selectedLanguage, handleLanguageChange, languageOptions, resolveRequestedLanguage]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const tutorialEnabled = isTutorialUiSuppressionRequested();
    if (!tutorialEnabled) return;

    const bridge = (payload?: { tab?: string; language?: string }) => {
      const nextTab = (payload?.tab || '').trim();
      if (nextTab && nextTab !== activeTab) {
        setActiveTab(nextTab);
      }
      const requestedLanguage = (payload?.language || '').trim();
      if (requestedLanguage) {
        const resolvedLanguage = resolveRequestedLanguage(requestedLanguage);
        if (resolvedLanguage && resolvedLanguage !== selectedLanguage) {
          handleLanguageChange(resolvedLanguage);
        }
      }
    };

    (window as any).__tutorialSetMetadataState = bridge;
    return () => {
      if ((window as any).__tutorialSetMetadataState === bridge) {
        delete (window as any).__tutorialSetMetadataState;
      }
    };
  }, [activeTab, handleLanguageChange, selectedLanguage, languageOptions, resolveRequestedLanguage]);

  const formatDetectionTimestamp = (value?: string | null) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toLocaleString();
  };

  const handleFrenchNarrativeToneChange = async (value?: FrenchNarrativeTone) => {
    const previousTone = editBook.frenchNarrativeTone;
    handleInputChange('frenchNarrativeTone', value, { markDirty: false });
    setFrenchToneRecentlySaved(false);

    const userId = auth?.currentUser?.uid;
    if (!userId) {
      message.error('You must be signed in to change this setting.');
      handleInputChange('frenchNarrativeTone', previousTone, { markDirty: false });
      return;
    }

    setIsSavingFrenchTone(true);
    try {
      const response = await axios.post(`${apiBase}/books/french-tone`, {
        bookId,
        userId,
        frenchNarrativeTone: value ?? null,
      });

      if (response.data?.status !== 'success') {
        throw new Error(response.data?.message || 'Unable to save French narrative voice.');
      }

      onFrenchToneSaved?.(value);
      message.success('French narrative voice saved');
      setFrenchToneRecentlySaved(true);
      if (frenchToneSaveTimeoutRef.current) {
        window.clearTimeout(frenchToneSaveTimeoutRef.current);
      }
      frenchToneSaveTimeoutRef.current = window.setTimeout(() => {
        setFrenchToneRecentlySaved(false);
        frenchToneSaveTimeoutRef.current = null;
      }, 2500);
    } catch (error) {
      console.error('Failed to save French narrative tone', error);
      const errorMessage =
        (error as any)?.response?.data?.detail ||
        (error as any)?.message ||
        'Unable to save French narrative voice. Please try again.';
      message.error(errorMessage);
      handleInputChange('frenchNarrativeTone', previousTone, { markDirty: false });
    } finally {
      setIsSavingFrenchTone(false);
    }
  };

  const handleRunFrenchNarrativeToneDetection = async () => {
    if (isDetectingFrenchTone) {
      return;
    }
    const userId = auth?.currentUser?.uid;
    if (!userId) {
      message.error('You must be signed in to use AI detection.');
      return;
    }
    setIsDetectingFrenchTone(true);
    try {
      const response = await axios.post(`${apiBase}/books/french-tone/detect`, {
        bookId,
        userId,
      }, { headers: await getAuthHeaders() });
      const data = response.data || {};
      if (data?.status !== 'success') {
        throw new Error(data?.message || 'Unable to detect French narrative voice.');
      }
      const detectedToneRaw = typeof data?.tone === 'string' ? data.tone.trim().toLowerCase() : '';
      if (isFrenchToneValue(detectedToneRaw)) {
        handleInputChange('frenchNarrativeTone', detectedToneRaw, { markDirty: false });
        onFrenchToneSaved?.(detectedToneRaw);
      }
      const detectedTimestamp = data?.detectedAt || new Date().toISOString();
      setFrenchToneDetectedAt(detectedTimestamp);
      message.success(data?.message || 'French narrative voice detected.');
      setFrenchToneRecentlySaved(true);
      if (frenchToneSaveTimeoutRef.current) {
        window.clearTimeout(frenchToneSaveTimeoutRef.current);
      }
      frenchToneSaveTimeoutRef.current = window.setTimeout(() => {
        setFrenchToneRecentlySaved(false);
        frenchToneSaveTimeoutRef.current = null;
      }, 2500);
    } catch (error) {
      console.error('Failed to detect French narrative tone', error);
      const errorMessage =
        (error as any)?.response?.data?.detail ||
        (error as any)?.response?.data?.message ||
        (error as any)?.message ||
        'Unable to detect French narrative voice. Please try again.';
      message.error(errorMessage);
    } finally {
      setIsDetectingFrenchTone(false);
    }
  };

  const handleOriginalLanguageChange = (value: string) => {
    if (value === bookLanguage) return;
    Modal.confirm({
      title: 'Change original language?',
      content: (
        <div>
          <p>The Language here is the book's original/source language. Only change this if it was set incorrectly when you uploaded the book.</p>
          <p>This is NOT the language you want to translate into. To translate metadata, use "Add Language" in the Language menu above or the Translate metadata button.</p>
          <p>Do you want to change the original language to "{value}"?</p>
        </div>
      ),
      okText: 'Yes, change it',
      cancelText: 'Keep current',
      onOk: () => {
        setBookLanguage(value);
        setHasUnsavedChanges(true);
        message.success(`${value} language updated.`);
      },
    });
  };

  const handleCopyOriginalBlurb = async () => {
    if (typeof navigator === 'undefined' || !navigator.clipboard) {
      message.error('Clipboard not available');
      return;
    }

    const htmlValue = sanitizeRichTextHtml(blurb);

    try {
      const tempElement = document.createElement('div');
      tempElement.innerHTML = htmlValue;
      const plainText = tempElement.textContent || tempElement.innerText || '';

      const clipboardItemCtor = typeof window !== 'undefined' ? (window as any).ClipboardItem : undefined;

      if (clipboardItemCtor && navigator.clipboard.write) {
        await navigator.clipboard.write([
          new clipboardItemCtor({
            'text/plain': new Blob([plainText], { type: 'text/plain' }),
            'text/html': new Blob([htmlValue], { type: 'text/html' }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(plainText);
      }

      message.success('Blurb copied to clipboard');
    } catch (error) {
      console.error('Failed to copy blurb', error);
      message.error('Failed to copy blurb');
    }
  };

  const runAdvancedMetadataAutofill = async (options?: {
    forceRegenerate?: boolean;
    applyFields?: string[];
    applyValues?: Record<string, string[]>;
    openReviewOnSuccess?: boolean;
  }) => {
    if (!onRunMetadataAutofill) return null;
    setMetadataAutofillLoading(true);
    try {
      const result = await onRunMetadataAutofill({
        mode: 'existing_suggestions',
        applyMode: 'suggestions_only',
        forceRegenerate: Boolean(options?.forceRegenerate),
        applyFields: options?.applyFields,
        applyValues: options?.applyValues,
      });
      if (result?.status === 'success' && options?.openReviewOnSuccess) {
        setMetadataAutofillReviewVisible(true);
      }
      return result;
    } finally {
      setMetadataAutofillLoading(false);
    }
  };

  const renderMetadataAutofillConfirmModal = () => (
    <Modal
      title={
        <div className="translation-modal-titlebar">
          <span className="translation-modal-titlebar__title">Suggest AI metadata</span>
        </div>
      }
      visible={metadataAutofillConfirmVisible}
      onCancel={() => setMetadataAutofillConfirmVisible(false)}
      width={720}
      className="metadata-autofill-confirm-modal"
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={() => setMetadataAutofillConfirmVisible(false)}>
          Cancel
        </Button>,
        <Button
          key="confirm"
          type="primary"
          className="app-primary-cta"
          icon={<BulbOutlined />}
          loading={metadataAutofillLoading}
          onClick={() => void runMetadataAutofillSuggestions()}
        >
          Generate suggestions
        </Button>,
      ]}
    >
      <div className="metadata-autofill-confirm-body" data-testid="tutorial:metadata:ai-confirm-modal">
        <section className="translation-modal-block">
          <div className="translation-modal-block-header">
            <h3>What ScribeShadow will do</h3>
            <p>Read book metadata and manuscript samples to suggest storefront and translation-prep fields.</p>
          </div>
          <div className="metadata-autofill-confirm-list">
            <div className="metadata-autofill-confirm-item metadata-autofill-confirm-item--wide">
              <strong>Suggestions only</strong>
              <span>Genres, tropes, keywords, and intended audiences are saved for review before you apply them.</span>
            </div>
            <div className="metadata-autofill-confirm-item">
              <strong>No credits used</strong>
              <span>This metadata scan is free and does not deduct from your credit balance.</span>
            </div>
            <div className="metadata-autofill-confirm-item">
              <strong>No overwrite</strong>
              <span>Existing fields stay unchanged unless you choose suggestions in the review step.</span>
            </div>
          </div>
        </section>
        <div className="metadata-autofill-confirm-note">
          Intended audience can influence translation tone and word choice, so review those suggestions carefully before applying them.
        </div>
      </div>
    </Modal>
  );

  const renderAiFilledFieldTag = (field: string) => {
    const appliedFields = Array.isArray(book?.metadataAutofill?.appliedFields)
      ? book.metadataAutofill.appliedFields
      : [];
    const manuallyEditedFields = Array.isArray(book?.metadataAutofill?.manuallyEditedFields)
      ? book.metadataAutofill.manuallyEditedFields
      : [];
    if (!appliedFields.includes(field) || manuallyEditedFields.includes(field)) {
      return null;
    }
    return <Tag color="blue">AI filled</Tag>;
  };

  const getBookTranslationContext = () => {
    const directContext = book?.bookTranslationContext;
    if (directContext && typeof directContext === 'object') {
      return directContext;
    }
    const autofillContext = book?.metadataAutofill?.bookTranslationContext;
    if (autofillContext && typeof autofillContext === 'object') {
      return autofillContext;
    }
    return null;
  };

  const formatTranslationContextValue = (value: any): React.ReactNode => {
    if (Array.isArray(value)) {
      return (
        <Space wrap>
          {value.filter(item => String(item || '').trim()).map(item => (
            <Tag key={String(item)}>{String(item)}</Tag>
          ))}
        </Space>
      );
    }
    if (value && typeof value === 'object') {
      return (
        <div className="metadata-context-signal-list">
          {Object.entries(value).map(([key, nestedValue]) => (
            <div key={key} className="metadata-context-signal-row">
              <span>{key}</span>
              <strong>{Array.isArray(nestedValue) ? nestedValue.join(', ') : String(nestedValue || '')}</strong>
            </div>
          ))}
        </div>
      );
    }
    return String(value || '');
  };

  const renderTranslationContextModal = () => {
    const context = getBookTranslationContext();
    if (!context) return null;
    const rows = [
      ['Primary subgenre', context.primarySubgenre],
      ['Point of view', context.pov],
      ['Tense', context.tense],
      ['Setting/time period', context.settingTimePeriod],
      ['Tone', context.tone],
      ['Content intensity', context.contentIntensity],
      ['Themes', context.themes],
      ['Title intent', context.titleIntent],
      ['Series positioning', context.seriesPositioning],
      ['Translator notes', context.translatorNotes],
      ['Genre signals', context.genreSignals],
    ].filter(([, value]) => {
      if (Array.isArray(value)) return value.length > 0;
      if (value && typeof value === 'object') return Object.keys(value).length > 0;
      return String(value || '').trim().length > 0;
    });
    return (
      <Modal
        title="Translation context"
        visible={translationContextModalVisible}
        onCancel={() => setTranslationContextModalVisible(false)}
        width={820}
        footer={[
          <Button key="close" type="primary" onClick={() => setTranslationContextModalVisible(false)}>
            Close
          </Button>,
        ]}
      >
        <div className="metadata-context-detail-list">
          {rows.map(([label, value]) => (
            <div key={String(label)} className="metadata-context-detail-row">
              <span>{label}</span>
              <div>{formatTranslationContextValue(value)}</div>
            </div>
          ))}
        </div>
      </Modal>
    );
  };

  useEffect(() => {
    if (isTutorialUiSuppressionRequested()) return;
    // A Marketing link's validated edition takes precedence over the parent's
    // initial Original tab. Synchronize the parent before normal tab changes resume.
    if (marketingEntryEditionRef.current) {
      const edition = marketingEntryEditionRef.current;
      marketingEntryEditionRef.current = null;
      handleLanguageChange(edition);
      return;
    }
    if (selectedTab) {
      if (selectedTab === 'Original') {
        setSelectedLanguage('original');
      } else {
        setSelectedLanguage(selectedTab);
      }
    }
    // Callback identity changes are not a request to switch language.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab]);

  useEffect(() => {
    return () => {
      if (frenchToneSaveTimeoutRef.current) {
        window.clearTimeout(frenchToneSaveTimeoutRef.current);
        frenchToneSaveTimeoutRef.current = null;
      }
    };
  }, []);

  const dashboardPendingProofreadReviewCount = useMemo(() => {
    const activeDashboardVersion = (() => {
      const languageValue = String(selectedLanguage || '').trim();
      if (languageValue && languageValue.toLowerCase() !== 'original') {
        return languageValue;
      }
      if (languageValue.toLowerCase() === 'original') {
        return 'Original';
      }
      return String(selectedTab || '').trim() || 'Original';
    })();

    if (!activeDashboardVersion || activeDashboardVersion === 'Original') {
      return (book?.chapters || []).filter((chapter: any) => hasPendingProofreadReviewForRow(chapter)).length;
    }
    const { sourceLang, targetLang } = splitLanguagePair(activeDashboardVersion);
    if (!sourceLang || !targetLang) {
      return 0;
    }
    const translatedChaptersKey = `translated_chapters_${sourceLang}_${targetLang}`;
    const translatedChapters = (book?.[translatedChaptersKey] || []) as any[];
    return translatedChapters.filter((chapter: any) => hasPendingProofreadReviewForRow(chapter)).length;
  }, [book, selectedLanguage, selectedTab]);

  const dashboardTabLabel = (
    <span
      data-testid="tutorial:navigation:dashboard-tab"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
    >
      {dashboardPendingProofreadReviewCount > 0 ? (
        <span
          data-testid="dashboard-tab-pending-indicator"
          aria-label={`Pending proofread reviews: ${dashboardPendingProofreadReviewCount}`}
          title={`${dashboardPendingProofreadReviewCount} pending proofread review${dashboardPendingProofreadReviewCount === 1 ? '' : 's'}`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: '#ff4d4f',
            boxShadow: '0 0 0 2px rgba(255, 77, 79, 0.18)',
            flexShrink: 0,
          }}
        />
      ) : null}
      <span>Dashboard</span>
    </span>
  );

  const renderDashboardContent = () => (
    <BookDashboardTab
      showTranslationProgress={showTranslationProgress}
      translationProgress={translationProgress}
      translationLanguages={translationLanguages}
      onSelectTranslationLanguage={onSelectTranslationLanguage}
      onEditMetadata={handleOpenMetadataTab}
      onAddLanguage={handleAddLanguage}
      onCreateAudiobook={handleOpenAudiobookTab}
      onExportBook={handleExportBookClick}
      onUploadChapters={onOpenChapterUploadModal}
      onTranslateBook={handleTranslateBookClick}
      onProofreadBook={handleProofreadBookClick}
      userId={auth?.currentUser?.uid || resolvedUserId}
      bookId={bookId}
      translationTrackerData={translationTrackerData}
      selectedLanguage={selectedLanguage}
      recentExports={recentExports}
      onDownloadRecentExport={onDownloadRecentExport}
      onRefreshProgress={onRefreshProgress}
      isRefreshingProgress={isRefreshingProgress}
      availableLanguages={languages}
      bookLanguage={bookLanguage}
      translationPairs={book.translation_pairs}
      keywordTranslations={keywordTranslations}
      neverTranslateWords={neverTranslateWords}
      alwaysTranslateWords={alwaysTranslateWords}
      accountNeverTranslateWords={accountNeverTranslateWords}
      accountAlwaysTranslateWords={accountAlwaysTranslateWords}
      seriesNeverTranslateWords={seriesNeverTranslateWords}
      seriesAlwaysTranslateWords={seriesAlwaysTranslateWords}
      languageCovers={languageCovers}
      book={book}
      onOpenMetadataForLanguage={openMetadataForLanguage}
      onOpenMetadataTranslateForLanguage={openMetadataTranslateForLanguage}
      onOpenKeywordSuggestionsForLanguage={openKeywordSuggestionsForLanguage}
      onOpenCoverTranslationForLanguage={onOpenCoverTranslationForLanguage}
      onOpenCoverHistoryForLanguage={onOpenCoverHistoryForLanguage}
      onOriginalCoverUpload={handleCoverUpload}
      onOpenProofreadForLanguage={onOpenProofreadForLanguage}
      onApplyPendingProofreadReviewsForLanguage={onApplyPendingProofreadReviewsForLanguage}
      applyingPendingProofreadLanguage={applyingPendingProofreadLanguage}
      onOpenGlossaryScanForLanguage={openGlossaryScanForLanguage}
      onOpenRelationshipAnalysis={onOpenRelationshipAnalysis}
      onOpenCharactersTab={RELATIONSHIP_UI_ENABLED ? handleOpenCharactersTab : undefined}
      onReturnToOriginalDashboard={handleReturnToOriginalDashboard}
      onSelectDashboardLanguage={handleOpenDashboardForLanguage}
      isUploadDisabled={isChapterUploadDisabled}
      uploadDisabledReason={chapterUploadDisabledReason}
      hasTitle={Boolean(editBook.title?.trim())}
      hasAuthor={Boolean(editBook.author?.trim())}
      hasBlurb={Boolean(blurb?.trim())}
      hasGenres={Array.isArray(genres) && genres.length > 0}
      hasKeywords={Array.isArray(keywords) && keywords.length > 0}
      hasCover={Boolean(languageCovers?.original || book.coverUrl)}
      frenchDialogueLegacyEligibilityByPair={frenchDialogueLegacyEligibilityByPair}
      onFetchFrenchDialogueLegacyEligibility={onFetchFrenchDialogueLegacyEligibility}
      onRunFrenchDialogueLegacyCorrection={onRunFrenchDialogueLegacyCorrection}
      onDashboardOverflowActionsChange={handleDashboardOverflowActionsChange}
    />
  );

  // Render content based on selected language
const renderContent = () => {
  if (activeTab === 'marketing') {
    const m2mTranslationContext = getBookTranslationContext();
    const m2mGenreDescription = buildM2MGenreDescription(
      m2mTranslationContext,
      displayedGenres,
      displayedTropes,
      displayedKeywords
    );
    const m2mNotes = buildM2MNotes(m2mTranslationContext);
    return (
      <MarketingTab
        apiBase={apiBase}
        bookId={bookId}
        userId={auth?.currentUser?.uid || resolvedUserId}
        bookTitle={book?.title}
        bookLanguage={bookLanguage || book?.language}
        selectedLanguage={selectedLanguage}
        completedTranslationPairs={book.translation_pairs || []}
        translationProgress={translationProgress || book?.translation_progress || {}}
        m2mGenreDescription={m2mGenreDescription}
        m2mNotes={m2mNotes}
        actionSignal={marketingActionSignal}
        creditBalance={creditBalance}
        fetchCreditBalance={fetchCreditBalance}
        onActiveModuleChange={setActiveMarketingModule}
        onGenerateBlurb={handleGenerateBlurb}
        onSuggestKeywords={() => toggleKeywordSuggestionModal(bookLanguage)}
      />
    );
  }
  if (selectedLanguage === 'original') {
    // Original language content
	if (activeTab === 'metadata') {
      const hasOriginalCover = Boolean(languageCovers?.original || book.coverUrl);
      const originalMetadataReadinessItems = buildSourceReadinessItems({
        hasTitle: Boolean(editBook.title?.trim()) && isTitleValid,
        hasAuthor: Boolean(editBook.author?.trim()),
        hasBlurb: hasVisibleText(blurb),
        hasGenres: displayedGenres.length > 0,
        hasKeywords: displayedKeywords.length > 0,
        hasCover: hasOriginalCover,
      });
      const originalMetadataReadyCount = originalMetadataReadinessItems.filter(item => item.done).length;
      const sourceMetadataTitle = editBook.title?.trim() || book.title?.trim() || 'Untitled book';
      const sourceMetadataAuthor = editBook.author?.trim() || book.author?.trim() || 'Author not set';
      const sourceChapterCount = Array.isArray(book.chapters) ? book.chapters.length : 0;
      return (
        <OriginalMetadataEditor
          hasUnsavedChanges={hasUnsavedChanges}
          isTitleValid={isTitleValid}
        >
            <section className="metadata-original-workbench dashboard-dominant-surface book-detail-primary-surface book-detail-workbench-stack" data-testid="tutorial:metadata:original-form">
              <div className="metadata-original-command-row book-detail-workbench-section">
                <div className="metadata-original-source-summary" aria-label="Source metadata overview">
                  <div className="metadata-original-source-main dashboard-book-identity">
                    <div className="metadata-original-cover-command">
                      {coverSection}
                    </div>
                    <div className="metadata-original-source-copy dashboard-book-identity-copy dashboard-cover-copy">
                      <h2>{sourceMetadataTitle}</h2>
                      <p>{sourceMetadataAuthor}</p>
                      <div className="metadata-original-source-stats dashboard-cover-facts" aria-label="Source workspace facts">
                        <span><strong>{bookLanguage}</strong> source</span>
                        <span><strong>{sourceChapterCount}</strong> {sourceChapterCount === 1 ? 'chapter' : 'chapters'}</span>
                      </div>
                    </div>
                  </div>
                  <section
                    id="metadata-blurb"
                    className="metadata-identity-blurb metadata-original-blurb-card"
                    data-tour-id="metadata-blurb"
                    data-testid="tutorial:metadata:blurb"
                    tabIndex={-1}
                  >
                    <Form.Item
                      style={{ marginBottom: 0 }}
                    >
                      <div className="metadata-blurb-field-header">
                        <span className="metadata-blurb-label">Blurb</span>
                        <div className="metadata-blurb-tool-actions">
                          <Tooltip title="Generate three blurb options from this manuscript">
                            <Button
                              size="small"
                              aria-label="Generate Blurb"
                              data-testid="tutorial:marketing:generate-blurb"
                              onClick={handleGenerateBlurb}
                              icon={<BulbOutlined />}
                            >
                              Generate blurb
                            </Button>
                          </Tooltip>
                          <Tooltip title="Copy blurb to clipboard">
                            <Button
                              size="small"
                              aria-label="Copy blurb"
                              onClick={handleCopyOriginalBlurb}
                              icon={<CopyOutlined />}
                            />
                          </Tooltip>
                          <Tooltip title="Export blurb to TXT">
                            <Button
                              size="small"
                              aria-label="Export Blurb to TXT"
                              onClick={exportBlurbToTxt}
                              icon={<ExportOutlined />}
                            />
                          </Tooltip>
                        </div>
                      </div>
                      <div data-testid="tutorial:metadata:blurb-editor">
                        <ReactQuill
                          className="metadata-blurb-editor"
                          value={sanitizeRichTextHtml(blurb)}
                          onChange={(value, _delta, source) => {
                            if (source !== 'user') {
                              return;
                            }
                            const previousBlurb = blurb;
                            setBlurb(value);
                            if (
                              normalizeMetadataHtmlForDirtyCheck(value) ===
                              normalizeMetadataHtmlForDirtyCheck(previousBlurb)
                            ) {
                              return;
                            }
                            if (shouldSuppressBlurbDirty?.()) {
                              return;
                            }
                            setHasUnsavedChanges(true);
                          }}
                          modules={{
                            toolbar: [
                              ['bold', 'italic', 'underline'],
                              ['clean']
                            ]
                          }}
                          placeholder="Enter book blurb"
                        />
                        <BlurbWordCounter value={blurb} />
                      </div>
                    </Form.Item>
                  </section>
                </div>
                <div className="metadata-original-command-stack">
                  <div className="metadata-original-readiness-strip dashboard-readiness-strip">
                    <div className="dashboard-source-readiness dashboard-source-readiness--command">
                      <div className="dashboard-readiness-strip-header">
                        <span>Source readiness</span>
                        <strong>
                          {originalMetadataReadyCount}/{originalMetadataReadinessItems.length} ready
                        </strong>
                      </div>
                      <div className="dashboard-completeness-row">
                        {originalMetadataReadinessItems.map(item => {
                          const statusLabel = item.done ? 'Ready' : 'Missing';
                          const accessibleLabel = `${item.label}: ${statusLabel}`;
                          return (
                            <Tooltip key={item.key} title={accessibleLabel}>
                              <button
                                type="button"
                                aria-label={accessibleLabel}
                                className={`dashboard-readiness-chip dashboard-readiness-chip--${item.done ? 'ready' : 'needed'}`}
                                onClick={() => handleOriginalMetadataReadinessClick(item)}
                              >
                                {item.label}: {statusLabel}
                              </button>
                            </Tooltip>
                          );
                        })}
                      </div>
                    </div>
                  </div>
              <MetadataIdentitySection
                primaryFields={(
                  <>
                    <Form.Item label={<strong>Title</strong>} required style={{ marginBottom: '12px' }} labelCol={{ style: { marginBottom: 0, lineHeight: '1' } }}>
                      <Input
                        data-testid="tutorial:metadata:title-input"
                        value={editBook.title}
                        onChange={(e) => handleInputChange('title', e.target.value)}
                        placeholder="Enter book title"
                      />
                    </Form.Item>
                    <Form.Item label={<strong>Author</strong>} style={{ marginBottom: '12px' }} labelCol={{ style: { marginBottom: 0, lineHeight: '1' } }}>
                      <Input
                        data-testid="tutorial:metadata:author-input"
                        value={editBook.author}
                        onChange={(e) => handleInputChange('author', e.target.value)}
                        placeholder="Enter author name"
                      />
                    </Form.Item>
                  </>
                )}
                secondaryFields={(
                  <>
                    <Form.Item
                      className="metadata-series-form-item"
                      label={<strong>Series</strong>}
                      style={{ marginBottom: '12px' }}
                      labelCol={{ style: { marginBottom: 0, lineHeight: '1' } }}
                    >
                      <Space.Compact className="metadata-series-control" style={{ width: '100%' }}>
                        <AutoComplete
                          className="metadata-series-autocomplete"
                          style={{ flex: 1 }}
                          value={editBook.series}
                          options={seriesOptions.map(option => ({
                            value: option.name,
                            label: option.name,
                            seriesId: option.seriesId,
                          }))}
                          filterOption={(inputValue, option) =>
                            (option?.value || '').toLowerCase().includes(inputValue.toLowerCase())
                          }
                          onChange={(value) => {
                            handleInputChange('series', value || '');
                          }}
                          onSelect={(value, option) => {
                            handleInputChange('series', value);
                            const foundId = (option as any)?.seriesId || null;
                            onSeriesChange(foundId || null);
                          }}
                        >
                          <Input
                            allowClear
                            className="metadata-series-input"
                            data-testid="tutorial:metadata:series-input"
                            placeholder="Enter or select a series"
                          />
                        </AutoComplete>
                        <Tooltip title="Open series settings">
                          <Button
                            data-tour-id="metadata-edit-series"
                            icon={<FormOutlined />}
                            onClick={() => openSeriesManager()}
                            aria-label="Open series settings"
                          />
                        </Tooltip>
                      </Space.Compact>
                    </Form.Item>
                    <Form.Item label={<strong>Series Book Number</strong>} style={{ marginBottom: '12px' }} labelCol={{ style: { marginBottom: 0, lineHeight: '1' } }}>
                      <Input
                        data-testid="tutorial:metadata:series-book-number-input"
                        value={editBook.booknumber}
                        onChange={e => handleInputChange('booknumber', e.target.value)}
                        placeholder="Enter book number in series"
                      />
                    </Form.Item>
                    <Form.Item
                      label={(
                        <Space>
                          <strong>Book source language</strong>
                          <Tooltip title="The language this manuscript was written or uploaded in. Changing it can affect translation source settings.">
                            <QuestionCircleOutlined />
                          </Tooltip>
                        </Space>
                      )}
                      style={{ marginBottom: '12px' }}
                      labelCol={{ style: { marginBottom: 0, lineHeight: '1' } }}
                    >
                      <Select
                        data-testid="tutorial:metadata:book-language-select"
                        value={bookLanguage}
                        onChange={handleOriginalLanguageChange}
                      >
                        {languages.map(lang => (
                          <Select.Option key={lang} value={lang}>
                            <span>
                              {lang}
                              {isLanguageBeta(lang) && <BetaTag />}
                            </span>
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </>
                )}
              />
              <MetadataPositioningSection
                status={readerDiscoveryReadiness}
                dataTestId="tutorial:metadata:positioning-section"
              >
                <Form.Item
                  className="metadata-genres-form-item"
                  label={
                    <Space>
                      <strong>Genres</strong>
                      {renderAiFilledFieldTag('genre')}
                      <Tooltip title="Enter or paste genres individually (comma-separated lists are split). ScribeShadow uses them to guide tone and translation choices.">
                        <QuestionCircleOutlined />
                      </Tooltip>
                    </Space>
                  }
                  style={{ marginBottom: '12px' }}
                  labelCol={{ style: { marginBottom: 0, lineHeight: '1' } }}
                >
                  <Select
                    data-testid="tutorial:metadata:genres-select"
                    className="metadata-compact-tag-select"
                    mode="tags"
                    style={{ width: '100%' }}
                    placeholder="Type or select genres"
                    value={displayedGenres}
                    tagRender={createTagRenderer(seriesGenreSet)}
                    maxTagCount={METADATA_TAG_MAX_COUNT}
                    maxTagTextLength={METADATA_TAG_MAX_TEXT_LENGTH}
                    maxTagPlaceholder={renderMetadataMaxTagPlaceholder}
                    onChange={values => handleSeriesListChange(values as string[], seriesGenreDefaults, setGenres)}
                    popupRender={menu => (
                      <>
                        {menu}
                        <Divider style={{ margin: '4px 0' }} />
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                          gap: '10px',
                          padding: 8
                        }}>
                          {commonGenres.map(genre => (
                            <Tag
                              key={genre}
                              style={{ cursor: 'pointer', textAlign: 'center' }}
                              onClick={() =>
                                handleSeriesListChange([...displayedGenres, genre], seriesGenreDefaults, setGenres)
                              }
                            >
                              {genre}
                            </Tag>
                          ))}
                        </div>
                      </>
                    )}
                  >
                    {genres.map(genre => (
                      <Select.Option key={genre} value={genre}>{genre}</Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  className="metadata-tropes-form-item"
                  label={
                    <Space>
                      <strong>Tropes</strong>
                      {renderAiFilledFieldTag('tropes')}
                      <Tooltip title="Enter or paste tropes; comma-separated lists automatically split into individual entries. ScribeShadow uses them when generating keyword suggestions.">
                        <QuestionCircleOutlined />
                      </Tooltip>
                    </Space>
                  } style={{ marginBottom: '12px' }} labelCol={{ style: { marginBottom: 0, lineHeight: '1' } }}
                >
                  <Select
                    data-testid="tutorial:metadata:tropes-select"
                    className="metadata-compact-tag-select"
                    mode="tags"
                    style={{ width: '100%' }}
                    placeholder="Type or select tropes"
                    value={displayedTropes}
                    tagRender={createTagRenderer(seriesTropesSet)}
                    maxTagCount={METADATA_TAG_MAX_COUNT}
                    maxTagTextLength={METADATA_TAG_MAX_TEXT_LENGTH}
                    maxTagPlaceholder={renderMetadataMaxTagPlaceholder}
                    tokenSeparators={[',']}
                    onChange={value =>
                      handleSeriesListChange(value as string[], seriesTropesDefaults, setTropes)
                    }
                  >
                    {tropes.map(trope => (
                      <Select.Option key={trope} value={trope}>{trope}</Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  className="metadata-intended-audience-item"
                  label={
                    <Space align="center" size={6}>
                      <strong>Intended audiences</strong>
                      {renderAiFilledFieldTag('intendedAudience')}
                      <Tooltip
                        title={(
                          <div style={{ maxWidth: 320 }}>
                            <div>
                              Enter or paste audiences individually; comma-separated values will split into multiple entries. Used to tailor tone and word choice in translations.
                            </div>
                            <div style={{ marginTop: 8 }}>
                              Click to learn more
                            </div>
                          </div>
                        )}
                      >
                        <span
                          role="button"
                          tabIndex={0}
                          className="metadata-label-help-trigger"
                          data-testid="tutorial:metadata:intended-audiences-info"
                          onClick={() => setIsAudienceInfoModalVisible(true)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              setIsAudienceInfoModalVisible(true);
                            }
                          }}
                          aria-label="Learn more about intended audiences"
                        >
                          <QuestionCircleOutlined />
                        </span>
                      </Tooltip>
                    </Space>
                  } style={{ marginBottom: '12px' }} labelCol={{ style: { marginBottom: 0, lineHeight: '1' } }}
                >
                  <Select
                    data-testid="tutorial:metadata:intended-audiences-select"
                    className="metadata-compact-tag-select"
                    mode="tags"
                    style={{ width: '100%' }}
                    placeholder="Type or select intended audiences"
                    value={displayedIntendedAudience}
                    tagRender={createTagRenderer(seriesIntendedSet)}
                    maxTagCount={METADATA_TAG_MAX_COUNT}
                    maxTagTextLength={METADATA_TAG_MAX_TEXT_LENGTH}
                    maxTagPlaceholder={renderMetadataMaxTagPlaceholder}
                    tokenSeparators={[',']}
                    onChange={vals => handleIntendedAudienceChange(vals as string[])}
                    popupRender={menu => (
                      <>
                        {menu}
                        <Divider style={{ margin: '4px 0' }} />
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                            gap: '10px',
                            padding: 8,
                          }}
                        >
                          {INTENDED_AUDIENCE_PRESETS.map(aud => (
                            <Tag
                              key={aud}
                              style={{
                                cursor: 'pointer',
                                textAlign: 'center',
                                width: '100%',
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                lineHeight: 1.2,
                                height: 'auto',
                                padding: '2px 6px',
                              }}
                              onClick={() => handleIntendedAudienceChange([...displayedIntendedAudience, aud])}
                            >
                              {aud}
                            </Tag>
                          ))}
                        </div>
                      </>
                    )}
                  />
                </Form.Item>
                <Form.Item
                  className="metadata-keywords-form-item"
                  style={{ marginBottom: '12px' }}
                  >
                  <div id="metadata-lists" tabIndex={-1} className="metadata-keywords-anchor">
                    <div className="metadata-keyword-label-row">
                      <Space>
                        <strong>Keywords</strong>
                        {renderAiFilledFieldTag('keywords')}
                        <Tooltip title="Enter or paste keywords (comma-separated lists are split) such as those used with Amazon. ScribeShadow uses them when generating keyword suggestions.">
                          <QuestionCircleOutlined />
                        </Tooltip>
                      </Space>
                      {!originalKeywordSuggestionsCoveredByAutofill && (
                        <Button
                          size="small"
                          icon={<BulbOutlined />}
                          onClick={() => toggleKeywordSuggestionModal(bookLanguage)}
                          data-tour-id="metadata-generate-keywords"
                          data-testid="tutorial:marketing:generate-keyword-suggestions"
                        >
                          <span data-testid="tutorial:metadata:keyword-suggestions">Suggest keywords</span>
                        </Button>
                      )}
                    </div>
                    <div className="metadata-keyword-control-row">
                      <Select
                        data-testid="tutorial:metadata:keywords-select"
                        className="metadata-compact-tag-select"
                        mode="tags"
                        style={{ width: '100%' }}
                        placeholder="Type or select keywords"
                        value={displayedKeywords}
                        tagRender={createTagRenderer(seriesKeywordsSet)}
                        maxTagCount={METADATA_KEYWORD_TAG_MAX_COUNT}
                        maxTagTextLength={METADATA_TAG_MAX_TEXT_LENGTH}
                        maxTagPlaceholder={renderMetadataMaxTagPlaceholder}
                        tokenSeparators={[',']}
                        onChange={value =>
                          handleSeriesListChange(value as string[], seriesKeywordsDefaults, setKeywords)
                        }
                      >
                        {keywords.map(keyword => (
                          <Select.Option key={keyword} value={keyword}>{keyword}</Select.Option>
                        ))}
                      </Select>
                    </div>
                  </div>
                  </Form.Item>
              </MetadataPositioningSection>
                </div>
              </div>

              <Modal
                title="Intended Audiences"
                visible={isAudienceInfoModalVisible}
                onOk={() => setIsAudienceInfoModalVisible(false)}
                onCancel={() => setIsAudienceInfoModalVisible(false)}
                footer={[
                  <Button
                    key="close"
                    type="primary"
                    data-testid="tutorial:metadata:intended-audiences-close"
                    onClick={() => setIsAudienceInfoModalVisible(false)}
                  >
                    Close
                  </Button>,
                ]}
              >
                <div data-testid="tutorial:metadata:intended-audiences-modal">
                  <p>
                    Intended audiences help ScribeShadow tailor translation tone, vocabulary, and reading level for both
                    chapters and metadata.
                  </p>
                  <p>
                    You can add multiple labels or create your own to reflect the readers you are targeting.
                  </p>
                  <Divider />
                  <div
                    style={{
                      background: 'var(--surface-muted)',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: 8,
                      padding: 12,
                    }}
                  >
                    <strong>Audiences with special translation logic</strong>
                    <div style={{ marginTop: 8 }}>
                      <strong>Clean Romance Readers:</strong> enables clean-language guardrails: no profanity or religious
                      exclamations, and euphemisms stay mild.
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <strong>MM Romance Readers:</strong> adds consistency guardrails for male/male romance dynamics and
                      helps prevent accidental feminization or heteronormative rewrites.
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <strong>FF Romance Readers:</strong> adds consistency guardrails for female/female romance dynamics
                      and helps prevent accidental masculinization or heteronormative rewrites.
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <strong>Nonfiction - Formal:</strong> uses formal direct reader address in languages that distinguish
                      it, such as German Sie/Ihnen/Ihr.
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <strong>Nonfiction - Informal:</strong> uses informal direct reader address in languages that
                      distinguish it, such as German du/dir/dich/dein.
                    </div>
                    <div style={{ marginTop: 8 }}>
                      Nonfiction reader-address options apply only to narrator or author address to the reader. Character
                      dialogue still follows scene context and relationship guidance.
                    </div>
                  </div>
                </div>
              </Modal>

              {renderMetadataAutofillConfirmModal()}
              <MetadataAutofillReviewModal
                open={metadataAutofillReviewVisible}
                loading={metadataAutofillLoading}
                run={book?.metadataAutofill}
                onStartTour={metadataAutofillReviewTour.start}
                onClose={() => {
                  setMetadataAutofillReviewVisible(false);
                  if (metadataAutofillReviewTour.open) {
                    void metadataAutofillReviewTour.close();
                  }
                }}
                onApply={(fields: MetadataAutofillFieldKey[], values: Record<string, string[]>) =>
                  runAdvancedMetadataAutofill({
                    applyFields: fields,
                    applyValues: values,
                  })
                }
              />
              <AppTour
                open={metadataAutofillReviewVisible && metadataAutofillReviewTour.open}
                steps={metadataAutofillReviewTourSteps}
                current={metadataAutofillReviewTour.current}
                onChange={metadataAutofillReviewTour.setCurrent}
                onClose={metadataAutofillReviewTour.close}
                onFinish={metadataAutofillReviewTour.close}
                zIndex={2600}
              />
              {renderTranslationContextModal()}

            </section>
        </OriginalMetadataEditor>
	    );
  }
    if (activeTab === 'audiobook') {
        return (
            <AudiobookTab
              bookId={bookId}
              book={book}
              auth={auth}
              bookLanguage={bookLanguage}
              audiobookVersionOptions={audiobookVersionOptions}
              onTranslateText={onTranslateText}
              onLoadingChange={setIsAudiobookLoading}
              isLangTesterUser={isLangTesterUser}
              chapterWordCounts={chapterWordCounts}
              isBetaUser={isBetaUser}
              languageCovers={languageCovers}
              startTourSignal={startAudiobookTourSignal}
              onTopUpCredits={onAudiobookTopUpCredits}
            />
        );
      }
    } else {
      // Translation content
	if (activeTab === 'metadata') {
	  const selectedTargetLang = splitLanguagePair(selectedLanguage).targetLang.replace(/0/g, ' ');
      const showFrenchNarrativeVoiceSelector = selectedTargetLang === 'French';
      const hasSourceSeries = Boolean((editBook.series || '').trim());
      const glossaryRuleCount = [
        resolveBookTerms(neverTranslateWords, selectedLanguage),
        resolveBookTerms(alwaysTranslateWords, selectedLanguage),
        resolveSeriesTerms(seriesNeverTranslateWords, selectedTargetLang, selectedLanguage),
        resolveSeriesTerms(seriesAlwaysTranslateWords, selectedTargetLang, selectedLanguage),
        resolveAccountTerms(accountNeverTranslateWords, selectedTargetLang),
        resolveAccountTerms(accountAlwaysTranslateWords, selectedTargetLang),
      ].reduce((count, terms) => count + terms.length, 0);
      const localizedGlossaryReady = glossaryRuleCount > 0;
      const localizedMetadataFields = [
        { key: 'title' as const, label: 'Title', value: titleTranslations[selectedLanguage] || '', originalValue: editBook.title || '' },
        ...(hasSourceSeries ? [{ key: 'series' as const, label: 'Series', value: seriesTranslations[selectedLanguage] || '', originalValue: editBook.series || '' }] : []),
        { key: 'blurb' as const, label: 'Blurb', value: blurbTranslations[selectedLanguage] || '', originalValue: blurb || '' },
      ];
      const languageGuidance = showFrenchNarrativeVoiceSelector ? (() => {
          const toneSelectValue = editBook.frenchNarrativeTone ?? AUTO_FRENCH_TONE_VALUE
          const isAutoToneSelected = toneSelectValue === AUTO_FRENCH_TONE_VALUE
          const detectionTimestampLabel = formatDetectionTimestamp(frenchToneDetectedAt)
          const handleToneSelectChange = (val: string) => {
            if (val === AUTO_FRENCH_TONE_VALUE) {
              handleFrenchNarrativeToneChange(undefined)
            } else {
              handleFrenchNarrativeToneChange(val as FrenchNarrativeTone)
            }
          }
          return (
          <div
            className="localized-language-behavior-card"
          >
            <div className="metadata-section-heading book-detail-section-heading localized-language-behavior-heading">
              <div>
                <h3>French-specific guidance</h3>
                <p>Narrative voice settings for this localized edition.</p>
              </div>
              {isAutoToneSelected && (
                <Button
                  className="localized-language-behavior-detect-button"
                  icon={<BulbOutlined />}
                  onClick={handleRunFrenchNarrativeToneDetection}
                  disabled={isSavingFrenchTone}
                  loading={isDetectingFrenchTone}
                >
                  Run AI Detection
                </Button>
              )}
            </div>
            <div className="localized-language-behavior-control">
              <label className="localized-language-behavior-label">
                <span>French narrative voice</span>
                <Tooltip title="Controls how ScribeShadow narrates full-book French translations. AI Detect recommends from source tense, POV, register, genre, and subgenre, but you can override it.">
                  <QuestionCircleOutlined />
                </Tooltip>
                <FrenchNarrationInfo linkStyle={{ marginLeft: 0 }} variant="simple" />
              </label>
              <div className="localized-language-behavior-input-row">
                <Select
                  className="localized-language-behavior-select"
                  value={toneSelectValue}
                  onChange={(value) => handleToneSelectChange(value as string)}
                  disabled={isSavingFrenchTone}
                  loading={isSavingFrenchTone}
                >
                  <Select.Option value={AUTO_FRENCH_TONE_VALUE}>
                    AI Detect (recommend voice)
                  </Select.Option>
                  <Select.Option value="modern">Modern/Accessible voice</Select.Option>
                  <Select.Option value="classical">Classical/Literary voice</Select.Option>
                  <Select.Option value="present">Present Tense voice</Select.Option>
                </Select>
              </div>
              {isAutoToneSelected && (
                <div className="localized-language-behavior-help">
                  {frenchToneDetectedAt
                    ? `AI detection last recommended a voice on ${detectionTimestampLabel || 'this book'}. You can rerun it while testing.`
                    : 'Let AI analyze a few chapters and recommend a voice. You can override the recommendation after it runs.'}
                </div>
              )}
              {!isAutoToneSelected && frenchToneDetectedAt && (
                <div className="localized-language-behavior-help">
                AI detection recommended a voice on {detectionTimestampLabel || 'this book'}. Your manual choice overrides it.
              </div>
              )}
              {isSavingFrenchTone && (
                <div className="localized-language-behavior-help">
                Saving French narrative voice…
              </div>
              )}
              {!isSavingFrenchTone && frenchToneRecentlySaved && (
                <div className="localized-language-behavior-help localized-language-behavior-help--success">
                French narrative voice saved.
              </div>
              )}
            </div>
          </div>
          )
        })() : (
          <div className="localized-language-behavior-card localized-language-behavior-card--placeholder">
            <div className="metadata-section-heading book-detail-section-heading localized-language-behavior-heading">
              <div>
                <h3>Language-specific guidance</h3>
                <p>No extra language controls are needed for this edition.</p>
              </div>
            </div>
            <div className="localized-language-behavior-help">
              No language-specific controls are needed for {selectedTargetLang || 'this language'} yet. Use glossary rules to protect names, phrases, and fixed translations.
            </div>
          </div>
        );
      const renderTranslatedMetadataContent = (
        contentMode: LocalizedMetadataContentMode,
        options?: { onManualMetadataSaved?: () => void },
      ) => {
        const metadataFieldsMode = ['hidden', 'manual', 'existing', 'all'].includes(contentMode)
          ? contentMode as 'hidden' | 'manual' | 'existing' | 'all'
          : 'all';
        return (
          <MetadataTranslationTabContent
            pair={selectedLanguage}
            titleTranslations={titleTranslations}
            seriesTranslations={seriesTranslations}
            seriesDescriptionTranslations={seriesDescriptionTranslations}
            blurbTranslations={blurbTranslations}
            keywordTranslations={keywordTranslations}
            neverTranslateWords={neverTranslateWords}
            alwaysTranslateWords={alwaysTranslateWords}
            accountNeverTranslateWords={accountNeverTranslateWords}
            accountAlwaysTranslateWords={accountAlwaysTranslateWords}
            seriesNeverTranslateWords={seriesNeverTranslateWords}
            seriesAlwaysTranslateWords={seriesAlwaysTranslateWords}
            bookId={bookId}
            auth={auth}
            handleDeleteTranslatedChapters={handleDeleteTranslatedChapters}
            handleDeleteProofreadChapters={handleDeleteProofreadChapters}
            setIsNeverTranslateModalVisible={setIsNeverTranslateModalVisible}
            setIsAlwaysTranslateModalVisible={setIsAlwaysTranslateModalVisible}
            setCurrentLanguagePair={setCurrentLanguagePair}
            setTitleTranslations={setTitleTranslations}
            setSeriesTranslations={setSeriesTranslations}
            setBlurbTranslations={setBlurbTranslations}
            onOpenSeriesManager={() => openSeriesManager('description-translations')}
            exportBlurbToTxt={exportBlurbToTxt}
            apiBase={apiBase}
            resolvedUserId={resolvedUserId}
            selectedSeriesId={selectedSeriesId}
            setNeverTranslateWords={setNeverTranslateWords}
            setAlwaysTranslateWords={setAlwaysTranslateWords}
            setAccountNeverTranslateWords={setAccountNeverTranslateWords}
            setAccountAlwaysTranslateWords={setAccountAlwaysTranslateWords}
            setSeriesTranslateRules={setSeriesTranslateRules}
            creditBalance={creditBalance}
            fetchCreditBalance={fetchCreditBalance}
            glossaryScanRequest={glossaryScanRequest}
            onGlossaryScanRequestHandled={() => setGlossaryScanRequest(null)}
            glossaryScanReviewRequest={glossaryScanReviewRequest || termScanReviewTrigger}
            onGlossaryScanReviewRequestHandled={() => setGlossaryScanReviewRequest(null)}
            translationProgress={translationProgress}
            onTopUpCredits={onTermScanTopUpCredits}
            onOpenKeywordSuggestions={() =>
              openKeywordSuggestionsForLanguage(selectedMetadataTargetLanguage || selectedLanguage)
            }
            contentMode={contentMode}
            metadataFieldsMode={metadataFieldsMode}
            onManualMetadataSaved={options?.onManualMetadataSaved}
            translationGuidanceLead={languageGuidance}
            storefrontCoverSection={metadataFieldsMode !== 'manual' ? coverSection : null}
            includeSeriesMetadata={hasSourceSeries}
          />
        );
      };
      const localizedKeywordCount = Array.isArray(keywordTranslations?.[selectedLanguage])
        ? keywordTranslations[selectedLanguage].length
        : 0;
      const selectedTargetCover = selectedTargetLang
        ? (languageCovers?.[selectedTargetLang] ?? languageCovers?.[selectedTargetLang.toLowerCase()])
        : undefined;
      const originalCoverCandidate = languageCovers?.original === ''
        ? ''
        : (languageCovers?.original || book.coverUrl || '');
      const localizedCoverReadiness = selectedTargetCover
        ? 'ready'
        : selectedTargetCover === ''
          ? 'missing'
          : originalCoverCandidate
            ? 'using_original'
            : 'missing';
	  return (
        <LocalizedMetadataWorkspace
          language={selectedTargetLang || selectedLanguage}
          authorName={editBook.author?.trim() || book.author?.trim() || 'Author not set'}
          fields={localizedMetadataFields}
          keywordCount={localizedKeywordCount}
          coverReadiness={localizedCoverReadiness}
          coverStatusLabel="Original cover shown"
          glossaryReady={localizedGlossaryReady}
          glossaryStatusLabel={localizedGlossaryReady ? `${glossaryRuleCount} rule${glossaryRuleCount === 1 ? '' : 's'}` : undefined}
          showGuidanceReadiness={showFrenchNarrativeVoiceSelector}
          guidanceReady
          guidanceStatusLabel="AI Detect ready"
          onSuggestKeywords={
            selectedMetadataTargetLanguage
              ? () => toggleKeywordSuggestionModal(selectedMetadataTargetLanguage)
              : undefined
          }
          onTranslateCover={
            selectedMetadataTargetLanguage && onOpenCoverTranslationForLanguage
              ? () => onOpenCoverTranslationForLanguage(selectedMetadataTargetLanguage)
              : undefined
          }
          onStartGlossaryScan={
            selectedMetadataTargetLanguage
              ? () => openGlossaryScanForLanguage(selectedMetadataTargetLanguage)
              : undefined
          }
          metadataContent={renderTranslatedMetadataContent}
        />
  );
}

else if (activeTab === 'audiobook') {
        return (
            <AudiobookTab
              bookId={bookId}
              book={book}
              selectedLanguage={selectedLanguage}
              auth={auth}
              bookLanguage={bookLanguage}
              audiobookVersionOptions={audiobookVersionOptions}
              onTranslateText={onTranslateText}
              onLoadingChange={setIsAudiobookLoading}
              isLangTesterUser={isLangTesterUser}
              chapterWordCounts={chapterWordCounts}
              isBetaUser={isBetaUser}
              languageCovers={languageCovers}
              startTourSignal={startAudiobookTourSignal}
              onTopUpCredits={onAudiobookTopUpCredits}
            />
        );
      }
    }
  };

  const metadataLanguageSelector = (
    <MetadataLanguageSelector
      value={selectedLanguage}
      options={languageOptions}
      onChange={handleLanguageChange}
      onAddLanguage={handleAddLanguage}
      searchValue={languageSearchValue}
      onSearchChange={setLanguageSearchValue}
    />
  );
  const languageScopedWorkspaceTabs = ['dashboard', 'metadata', 'audiobook', 'marketing'];
  const showWorkspaceLanguageSelector = languageScopedWorkspaceTabs.includes(activeTab);
  const selectedLanguageLabel =
    selectedLanguage === 'original'
      ? originalLanguageLabel
      : selectedMetadataTargetLanguage || selectedLanguage;
  const headerContext = (() => {
    if (activeTab === 'characters') {
      return {
        contextLabel: 'Cast & relationships',
        helperText: 'Manage book-wide names, aliases, Grammar Gender, and relationship guidance shared across every language.',
      };
    }
    if (activeTab === 'audiobook') {
      return {
        contextLabel: `${selectedLanguageLabel} audiobook`,
        helperText:
          selectedLanguage === 'original'
            ? 'Create and review audiobook output for the source language.'
            : 'Create and review audiobook output for this target language.',
      };
    }
    if (activeTab === 'dashboard') {
      return {
        contextLabel: `${selectedLanguageLabel} book dashboard`,
        helperText:
          selectedLanguage === 'original'
            ? 'Review source readiness, language progress, and the next publishing action.'
            : 'Review this language edition, store listing readiness, and the next publishing action.',
      };
    }
    if (activeTab === 'admin') {
      return {
        contextLabel: 'Translation insights',
        helperText: 'Review admin-only translation diagnostics for this book.',
      };
    }
    if (activeTab === 'marketing') {
      return {
        contextLabel: `${selectedLanguageLabel} marketing`,
        helperText:
          selectedLanguage === 'original'
            ? 'Order language-specific Novel Publicity Deep Dive reports for completed translations.'
            : 'Order and review Novel Publicity Deep Dive reports for this translated edition.',
      };
    }
    return {
      contextLabel: `${selectedLanguageLabel} metadata`,
      helperText:
        selectedLanguage === 'original'
          ? 'Edit the source details used for translations, store listings, and exports.'
          : 'Review translated metadata and language-specific publishing utilities.',
    };
  })();
  const tabBarExtraContent = showWorkspaceLanguageSelector
    ? {
        left: (
          <div className="metadata-tabs-language-context">
            {metadataLanguageSelector}
          </div>
        ),
      }
    : undefined;
  const metadataAutofillLoadingLabel = metadataAutofillReviewVisible
    ? 'Applying metadata suggestions…'
    : 'Reading the book and suggesting metadata…';

  return (
    <div ref={metadataTabShellRef} className="metadata-tab-shell" style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      // Add pointer-events styling to disable interactions when loading
      pointerEvents: isAudiobookLoading && activeTab === 'audiobook' ? 'none' : 'auto',
      opacity: isAudiobookLoading && activeTab === 'audiobook' ? 0.6 : 1,
      transition: 'opacity 0.2s ease'
    }}>
      {metadataAutofillLoading && (
        <div className="app-loading-overlay metadata-autofill-loading-overlay">
          <ProcessLoadingCard
            title={metadataAutofillLoadingLabel}
            description="This can take a moment."
          />
        </div>
      )}
	      <MetadataHeader
	        contextLabel={headerContext.contextLabel}
	        helperText={headerContext.helperText}
	        hasUnsavedChanges={activeTab === 'metadata' && selectedLanguage === 'original' && hasUnsavedChanges}
	        actions={renderHeaderActions()}
	      />

{/* Content tabs */}
<Tabs
  data-testid="tutorial:book-detail:tabs"
  className="metadata-section-tabs"
  activeKey={activeTab}
  onChange={setActiveTab}
  type="card"
  size="large"
  style={{
    flex: 1,
    display: 'flex',
    flexDirection: 'column'
  }}
  tabBarStyle={{ marginBottom: 0 }}
  tabBarExtraContent={tabBarExtraContent}
  destroyOnHidden
>
  <TabPane
    tab={dashboardTabLabel}
    key="dashboard"
    style={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    {renderDashboardContent()}
  </TabPane>
  <TabPane
    tab={<span data-testid="tutorial:navigation:metadata-tab">Metadata</span>}
    key="metadata"
    style={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}
  >
    {renderContent()}
  </TabPane>
  <TabPane
    tab={<span data-testid="tutorial:navigation:marketing-tab">Marketing</span>}
    key="marketing"
    style={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}
  >
    {renderContent()}
  </TabPane>
  {renderMetadataCharactersTab({
    bookId,
    apiBase,
    userId: resolvedUserId,
    onOpenMatrixEditor: onOpenRelationshipEditor,
    onOpenAnalysis: onOpenRelationshipAnalysis,
    onCloseMatrixEditor: onCloseRelationshipEditor,
    onCloseAnalysis: onCloseRelationshipAnalysis,
    startTourSignal: startCharactersTourSignal,
    relationshipSummaryCard,
    freeRunAvailable: relationshipFreeRunAvailable,
    analysisLoading: relationshipAnalysisLoading,
    analysisStatus: relationshipAnalysisStatus,
    registryRefreshSignal,
    registryClearSignal,
  })}
  <TabPane
    tab={<span data-tour-id="metadata-audiobook-tab" data-testid="tutorial:navigation:audiobook-tab">Audiobook</span>}
    key="audiobook"
    style={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}
  >
    {renderContent()}
  </TabPane>
  {isAdmin && (
    <TabPane
      tab="Admin"
      key="admin"
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <AdminTranslationInsights
        book={book}
        bookId={bookId}
        apiBase={apiBase}
        auth={auth}
        chapterWordCounts={chapterWordCounts}
        isActive={activeTab === 'admin'}
        isAdmin={isAdmin}
        onAskAI={onAskAI}
      />
    </TabPane>
  )}

</Tabs>
    </div>
  );
};

export default MetadataWorkspace;
